// Procedural isometric art. Every building and unit is drawn from simple
// shapes in code: no image assets yet, but each type has its own silhouette,
// damage shows as smoke, destruction as rubble, and hits as tracers.
import { Container, Graphics, Text } from 'pixi.js';
import type { ClaimRect, IncomingStrike } from '@thrash/sim';
import { MAP_SIZE, PLACE_MARGIN, TILE, isSeaTile, type Shot, type Snapshot } from '@thrash/sim';
import { depth, toScreen, unitsToScreen, TILE_H, TILE_W } from './iso.ts';
import { is3D, setBuildingFx3D, setBuildings3D, setUnits3D, setShots3D, setDeaths3D, setSmoke3D, setIncoming3D } from './view3d.ts';

export type BuildingState = 'normal' | 'selected' | 'ghost-ok' | 'ghost-bad' | 'dead';

export interface DrawableBuilding {
  type: string;
  x: number;
  y: number;
  size: number;
  hpFrac: number;
  level?: number;
  state?: BuildingState;
  /** Stable number for deterministic decoration (smoke, rubble). */
  seed?: number;
  /** Knocked out by an EMP: drawn with a flickering ring. */
  disabled?: boolean;
  /** Firing right now: a C-RAM spins its barrels up and flashes. */
  firing?: boolean;
}

// ---- palette -----------------------------------------------------------------

const GROUND_A = 0x2b3324;
const GROUND_B = 0x2f3828;
const GROUND_EDGE = 0x22281d;
const CONCRETE = 0x9a9890;
const STEEL = 0x7d848c;
const DARK = 0x2a2d31;
const SAND = 0xb8a170;
const OLIVE = 0x5d6b3a;
const RUST = 0x8a4b2a;
const MINES = new Set(['land_mine', 'at_mine', 'sea_mine']);
/** Defences whose rounds are missiles that fly to their target, with a smoke trail. */
export const MISSILE_LAUNCHERS = new Set(['missile_battery', 'missile_defence', 'bastion', 'atgm_post', 'patriot', 'thaad', 'tomahawk', 'himars', 'avenger', 'mortar']);
/** Shots that are missiles but hit at once in the sim (a Predator's Hellfire): drawn flying, like a howitzer's shell. */
export const MISSILE_SHOTS = new Set(['predator']);
/** Of those, the ones that fire low over the sea or the ground rather than up into the sky. */
export const LOW_LAUNCHERS = new Set(['missile_battery', 'bastion', 'atgm_post']);
const TEAM = 0x7ee787;       // attackers
const FLAG = 0xd14b4b;

/** Kept for code that wants a single colour per building, e.g. minimaps. */
export const COLORS: Record<string, number> = {
  hq: 0xe0b341, sentry: 0xd14b4b, barrier: CONCRETE, oil_derrick: 0x3f8fd6, fuel_tank: 0x2a5f91,
  steel_mill: STEEL, steel_yard: 0x5f5f5f, supply_depot: 0x9bc45a, warehouse: 0x5f7d33, staging_area: 0x8a6bc4,
  solar_array: 0x1f4e8c, wind_turbine: 0xe6ebf0, nuclear_reactor: 0xd8d8d0,
  farm: 0x7fa650, water_supply: 0x4fb3c9,
  shipyard: 0x7a8a96, sea_mine: 0x202830, fishing_boat: 0xc9a06a,
  missile_battery: 0x5a6070, tomahawk: 0x4e5866,
  bunker: 0x8a897f, electric_fence: 0x6fd3ff, land_mine: 0x5c5c3a, howitzer: 0x6b7048, air_defence: 0x7a8fa3, missile_defence: 0x9a6b3a, drone_interceptor: 0x4f8f8f,
  mortar: 0x6f6a3a, himars: 0x5b6b3a, avenger: 0x4a5d3a, gepard: 0x52634f, iron_beam: 0x8a8a90, hpm: 0x6d7268, phalanx: 0xd8dade,
};

/** Buildings that get staff walking round them while they run. */
const WORKER_TYPES = new Set(['oil_derrick', 'steel_mill', 'supply_depot', 'farm', 'water_supply']);

function shade(c: number, f: number): number {
  const r = Math.min(255, Math.round(((c >> 16) & 255) * f));
  const g = Math.min(255, Math.round(((c >> 8) & 255) * f));
  const b = Math.min(255, Math.round((c & 255) * f));
  return (r << 16) | (g << 8) | b;
}

/** Small deterministic hash for decoration placement. */
function rand(seed: number, i: number): number {
  let h = Math.imul(seed ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(i + 1, 0xc2b2ae35);
  h ^= h >>> 13; h = Math.imul(h, 0x27d4eb2f); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

// ---- primitives ----------------------------------------------------------------

type Pt = [number, number];
const P = (tx: number, ty: number): Pt => toScreen(tx, ty);

/**
 * The sides of a footprint that face the viewer, as screen edges. The corners
 * run clockwise on screen at every view angle, so an edge faces the viewer
 * exactly when it runs right to left. `light` is how lit the side is: sides
 * facing left catch the light, sides facing right are in shade.
 */
function visibleSides(pts: readonly Pt[]): { p: Pt; q: Pt; light: number }[] {
  const out: { p: Pt; q: Pt; light: number }[] = [];
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i]!, q = pts[(i + 1) % pts.length]!;
    const dx = q[0] - p[0], dy = q[1] - p[1];
    if (dx >= 0) continue;
    const nx = dy / (Math.hypot(dx, dy) || 1);   // outward normal's x, -1 (left) to 1 (right)
    out.push({ p, q, light: Math.min(0.8, Math.max(0.5, 0.635 - 0.19 * nx)) });
  }
  return out;
}

/** An isometric box on a tile-space rectangle, `h` screen pixels tall. */
function box(g: Graphics, x0: number, y0: number, x1: number, y1: number, h: number, color: number, alpha = 1): void {
  const a = P(x0, y0), b = P(x1, y0), c = P(x1, y1), d = P(x0, y1);
  for (const { p, q, light } of visibleSides([a, b, c, d])) {
    g.poly([p[0], p[1], q[0], q[1], q[0], q[1] - h, p[0], p[1] - h]).fill({ color: shade(color, light), alpha });
  }
  g.poly([a[0], a[1] - h, b[0], b[1] - h, c[0], c[1] - h, d[0], d[1] - h]).fill({ color, alpha });
  // a lighter lip along the top edges that face the viewer, so boxes read as solid
  if (h >= 6) {
    for (const { p, q } of visibleSides([a, b, c, d])) {
      g.moveTo(p[0], p[1] - h).lineTo(q[0], q[1] - h).stroke({ color: shade(color, 1.12), width: 1, alpha: 0.7 * alpha });
    }
  }
}

/** The walls of a box that face the viewer, as screen edges (see `box`). */
function boxSides(x0: number, y0: number, x1: number, y1: number): { p: Pt; q: Pt; light: number }[] {
  return visibleSides([P(x0, y0), P(x1, y0), P(x1, y1), P(x0, y1)]);
}

/**
 * Small rectangles (windows, doors, vents) laid across a wall running from p to
 * q and rising `h` pixels. The wall is cut into `cols` by `rows` cells and
 * `pick` chooses which cells get one, so detail stays per-type but reusable.
 */
function wallPanels(g: Graphics, p: Pt, q: Pt, h: number, cols: number, rows: number, color: number, alpha = 1,
  pick: (c: number, r: number) => boolean = () => true): void {
  const at = (u: number, v: number): Pt => [p[0] + (q[0] - p[0]) * u, p[1] + (q[1] - p[1]) * u - h * v];
  for (let c = 0; c < cols; c++) {
    for (let r = 0; r < rows; r++) {
      if (!pick(c, r)) continue;
      const [ax, ay] = at((c + 0.3) / cols, (r + 0.3) / rows);
      const [bx, by] = at((c + 0.7) / cols, (r + 0.3) / rows);
      const [cx, cy] = at((c + 0.7) / cols, (r + 0.7) / rows);
      const [dx, dy] = at((c + 0.3) / cols, (r + 0.7) / rows);
      g.poly([ax, ay, bx, by, cx, cy, dx, dy]).fill({ color, alpha });
    }
  }
}

/** A four-sided tent or pyramid on a tile-space square, its peak `h` pixels up. */
function pyramid(g: Graphics, x0: number, y0: number, x1: number, y1: number, h: number, color: number, alpha = 1): void {
  const corners = [P(x0, y0), P(x1, y0), P(x1, y1), P(x0, y1)];
  const [tx, ty] = P((x0 + x1) / 2, (y0 + y1) / 2);
  for (const { p, q, light } of visibleSides(corners)) {
    g.poly([p[0], p[1], q[0], q[1], tx, ty - h]).fill({ color: shade(color, light + 0.25), alpha });
  }
}

/** A flat diamond on the ground. */
function pad(g: Graphics, x0: number, y0: number, x1: number, y1: number, color: number, alpha = 1): void {
  const a = P(x0, y0), b = P(x1, y0), c = P(x1, y1), d = P(x0, y1);
  g.poly([a[0], a[1], b[0], b[1], c[0], c[1], d[0], d[1]]).fill({ color, alpha });
}

/** A vertical cylinder standing at tile point (cx, cy). r is in screen pixels. */
function cylinder(g: Graphics, cx: number, cy: number, r: number, h: number, color: number, alpha = 1, base = 0): void {
  const [sx, sy0] = P(cx, cy);
  const sy = sy0 - base;
  g.ellipse(sx, sy, r, r / 2).fill({ color: shade(color, 0.6), alpha });
  g.rect(sx - r, sy - h, r * 2, h).fill({ color: shade(color, 0.7), alpha });
  g.rect(sx, sy - h, r, h).fill({ color: shade(color, 0.58), alpha });
  g.ellipse(sx, sy - h, r, r / 2).fill({ color, alpha });
}

// ---- ground ----------------------------------------------------------------------

export function drawGrid(g: Graphics, shadeNoDeploy?: Uint8Array, claim?: ClaimRect | null): void {
  g.clear();
  for (let y = 0; y < MAP_SIZE; y++) {
    for (let x = 0; x < MAP_SIZE; x++) {
      const inside = x >= PLACE_MARGIN && y >= PLACE_MARGIN && x < MAP_SIZE - PLACE_MARGIN && y < MAP_SIZE - PLACE_MARGIN;
      const base = isSeaTile(x, y) ? WATER_NEAR : inside ? ((x + y) & 1 ? GROUND_A : GROUND_B) : GROUND_EDGE;
      pad(g, x, y, x + 1, y + 1, base);
      if (shadeNoDeploy && shadeNoDeploy[y * MAP_SIZE + x]) pad(g, x, y, x + 1, y + 1, 0xd14b4b, 0.13);
      // land the village has not claimed yet: inside the plan, outside the Command Center's reach
      if (claim && inside && !isSeaTile(x, y) && (x < claim.x0 || y < claim.y0 || x >= claim.x1 || y >= claim.y1)) pad(g, x, y, x + 1, y + 1, 0x000000, 0.28);
    }
  }
  if (claim) {
    const c = [P(claim.x0, claim.y0), P(claim.x1, claim.y0), P(claim.x1, claim.y1), P(claim.x0, claim.y1)];
    g.poly(c.flat()).stroke({ color: 0xe0b341, width: 1.5, alpha: 0.8 });
  }
  // map rim for a little depth, on whichever edges face the viewer
  const rim = [P(0, 0), P(MAP_SIZE, 0), P(MAP_SIZE, MAP_SIZE), P(0, MAP_SIZE)];
  for (const { p, q, light } of visibleSides(rim)) {
    g.poly([p[0], p[1], q[0], q[1], q[0], q[1] + 10, p[0], p[1] + 10]).fill({ color: shade(0x262d1f, light) });
  }
}

// ---- the land around the map --------------------------------------------------------

/** How many tiles of countryside are drawn around the playable map on each side. */
export const OUTSKIRTS = 40;
const BACKGROUND = 0x10140e;
const WATER_NEAR = 0x1d3b4c;
const WATER_FAR = 0x0d1e28;

function mix(a: number, b: number, f: number): number {
  const ch = (s: number) => Math.round(((a >> s) & 255) * (1 - f) + ((b >> s) & 255) * f);
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
}

/**
 * The country around the base: the sea to the west (past the sea column, see
 * isSeaTile), and on the other three sides ground that fades into the dark with
 * distance, patches of scrub and bare earth, a dirt road in from the east,
 * and trees, bushes and rocks. Drawn once, below everything else, and the
 * same every time (no randomness that is not seeded). Nothing out here is
 * part of the game: no building or unit can stand on it.
 */
export function drawOutskirts(g: Graphics): void {
  g.clear();
  const near = 0x283023;
  const N = MAP_SIZE;
  // how bright the ground is d tiles out from the map: full at the rim, background at the far edge
  const light = (d: number) => Math.max(0, 1 - d / OUTSKIRTS) ** 0.6;
  const distOut = (x: number, y: number) => Math.max(-x, -y, x - N, y - N, 0);
  // in bands that darken outwards, strips that never overlap, since painting nested
  // squares over each other cost every pixel twenty fills a frame
  const bands = 20;
  for (let k = 0; k < bands; k++) {
    const a = (k * OUTSKIRTS) / bands, b = ((k + 1) * OUTSKIRTS) / bands;
    // the sea to the west, the whole height of the view: deep near the coast, darker further out
    pad(g, -b, -OUTSKIRTS, -a, N + OUTSKIRTS, mix(WATER_NEAR, WATER_FAR, a / OUTSKIRTS));
    // land round the other three sides
    const color = mix(BACKGROUND, near, light((a + b) / 2));
    pad(g, 0, -b, N + b, -a, color);            // north
    pad(g, 0, N + a, N + b, N + b, color);      // south
    pad(g, N + a, -a, N + b, N + a, color);     // east
  }
  // a ripple here and there on the sea, drawn as one stroke so it stays cheap
  for (let y = -OUTSKIRTS; y < N + OUTSKIRTS; y++) {
    for (let x = -OUTSKIRTS; x < 0; x++) {
      if (rand(x * 37 + y * 911, 5) > 0.3) continue;
      const [sx, sy] = P(x + 0.3 + rand(x, y) * 0.4, y + 0.5);
      g.moveTo(sx - 6, sy).lineTo(sx + 6, sy);
    }
  }
  g.stroke({ color: 0x4f7d8f, width: 1, alpha: 0.35 });
  // patches of scrub and bare earth, in 2-tile cells
  for (let y = -OUTSKIRTS; y < N + OUTSKIRTS; y += 2) {
    for (let x = -OUTSKIRTS; x < N + OUTSKIRTS; x += 2) {
      const d = distOut(x + 1, y + 1);
      if (x < 0 || d < 1) continue;
      const r = rand(x * 131 + y, 7);
      if (r > 0.35) continue;
      const tone = r < 0.12 ? 0x3a3a26 : r < 0.24 ? 0x2c3a24 : 0x22291c;
      pad(g, x, y, x + 2, y + 2, mix(BACKGROUND, tone, light(d)), 0.9);
    }
  }
  // a dirt road coming in from the east to the edge of the map
  for (let x = N; x < N + OUTSKIRTS; x++) {
    const wobble = Math.round(Math.sin(x / 5) * 1.2);
    pad(g, x, 21 + wobble, x + 1, 23 + wobble, mix(BACKGROUND, 0x4a4030, light(x - N)));
  }
  // trees, bushes and rocks, back to front; clear of the map edge so nothing pokes over it, off the road, and never in the sea
  const props: { x: number; y: number; kind: number; s: number }[] = [];
  for (let y = -OUTSKIRTS; y < N + OUTSKIRTS; y++) {
    for (let x = -OUTSKIRTS; x < N + OUTSKIRTS; x++) {
      const d = distOut(x, y);
      if (x < 0 || d < 3) continue;
      if (x >= N && y >= 18 && y <= 26) continue;
      const r = rand(x * 7919 + y * 104729, 3);
      if (r > 0.075) continue;
      props.push({ x: x + rand(x, y) * 0.6, y: y + rand(y, x) * 0.6, kind: r < 0.045 ? 0 : r < 0.065 ? 1 : 2, s: 0.7 + rand(x + y, 11) * 0.6 });
    }
  }
  props.sort((a, b) => depth(a.x, a.y) - depth(b.x, b.y));
  for (const p of props) {
    const f = light(distOut(p.x, p.y));
    const [sx, sy] = P(p.x + 0.5, p.y + 0.5);
    if (p.kind === 0) {
      // a pine: shadow, trunk, two tiers of needles
      const h = TILE_H * 1.6 * p.s, w = TILE_W * 0.32 * p.s;
      g.ellipse(sx + 3, sy, w, w / 2).fill({ color: 0x000000, alpha: 0.25 * f });
      g.rect(sx - 1, sy - h * 0.35, 2, h * 0.35).fill({ color: mix(BACKGROUND, 0x4a3524, f) });
      g.poly([sx - w, sy - h * 0.3, sx + w, sy - h * 0.3, sx, sy - h]).fill({ color: mix(BACKGROUND, 0x2e4a2a, f) });
      g.poly([sx - w * 0.75, sy - h * 0.6, sx + w * 0.75, sy - h * 0.6, sx, sy - h * 1.25]).fill({ color: mix(BACKGROUND, 0x3a5c33, f) });
    } else if (p.kind === 1) {
      const r = 4 * p.s;
      g.ellipse(sx, sy - r * 0.5, r * 1.4, r).fill({ color: mix(BACKGROUND, 0x3d5530, f) });
      g.ellipse(sx - r * 0.5, sy - r * 0.9, r * 0.8, r * 0.6).fill({ color: mix(BACKGROUND, 0x4b6639, f) });
    } else {
      box(g, p.x + 0.2, p.y + 0.3, p.x + 0.2 + 0.5 * p.s, p.y + 0.3 + 0.4 * p.s, 4 * p.s, mix(BACKGROUND, 0x6f6c62, f));
    }
  }
}

// ---- buildings -------------------------------------------------------------------

/**
 * Draws one building: its footprint pad, then the body for its type (the switch
 * below), then the selection frame and health bar. To add a building type,
 * add a case to the switch that builds the body from box, cylinder, pad,
 * pyramid and wallPanels, and give it a colour in COLORS for minimaps.
 * With `fx` false the animated bits (flag, pump, smoke) are left out.
 */
function drawOne(g: Graphics, b: DrawableBuilding, t: number, fx = true, barriers?: ReadonlySet<string>): void {
  const { x, y, size } = b;
  const state = b.state ?? 'normal';
  const ghost = state === 'ghost-ok' || state === 'ghost-bad';
  const alpha = ghost ? 0.55 : 1;
  const tint = (c: number) => (state === 'ghost-bad' ? 0xff3b3b : c);
  const seed = b.seed ?? (x * 97 + y * 31);

  if (state === 'dead') {
    pad(g, x + 0.1, y + 0.1, x + size - 0.1, y + size - 0.1, 0x1d1f1a);
    for (let i = 0; i < 4 + size * 2; i++) {
      const rx = x + 0.2 + rand(seed, i) * (size - 0.6), ry = y + 0.2 + rand(seed, i + 50) * (size - 0.6);
      box(g, rx, ry, rx + 0.25 + rand(seed, i + 9) * 0.3, ry + 0.25, 2 + rand(seed, i + 3) * 4, shade(CONCRETE, 0.45 + rand(seed, i + 7) * 0.2));
    }
    return;
  }

  // footprint shadow and pad
  if (b.type !== 'barrier') pad(g, x + 0.05, y + 0.05, x + size - 0.05, y + size - 0.05, shade(GROUND_A, 0.7), alpha);
  const i0 = 0.18, i1 = size - 0.18;   // inset so neighbours do not touch
  const u = TILE_H;                    // vertical unit in pixels

  switch (b.type) {
    case 'hq': {
      box(g, x + i0, y + i0, x + i1, y + i1, u * 1.6, tint(0xb9a77a), alpha);
      box(g, x + 0.9, y + 0.9, x + size - 0.9, y + size - 0.9, u * 2.6, tint(0xcbb98b), alpha);
      // windows on the upper floor, a door in the middle of the lower one
      for (const s of boxSides(x + 0.9, y + 0.9, x + size - 0.9, y + size - 0.9)) {
        wallPanels(g, s.p, s.q, u * 2.6, 3, 2, shade(0x2b3a44, s.light), alpha, (c, r) => r === 1 || c !== 1);
      }
      const [ax, ay] = P(x + size - 1.1, y + 1.1);
      g.rect(ax - 0.5, ay - u * 4.4, 1, u * 1.8).fill({ color: DARK, alpha });
      if (fx) {
        const wave = Math.sin(t / 300) * 1.5;
        g.poly([ax + 0.5, ay - u * 4.4, ax + 9, ay - u * 4.1 + wave, ax + 0.5, ay - u * 3.8]).fill({ color: tint(FLAG), alpha });
      }
      break;
    }
    case 'sentry': {
      // the turret grows with its upgrades: a second barrel from the twin guns, red bands for explosive rounds, a gatling and radar on top
      const m = size / 2, lv = b.level ?? 1;
      cylinder(g, x + m, y + m, size * 6.5, u * 0.7, tint(lv >= 4 ? CONCRETE : SAND), alpha);
      cylinder(g, x + m, y + m, size * 3.2, u * 0.5, tint(OLIVE), alpha, u * 0.7);
      const [gx, gy] = P(x + m, y + m);
      g.rect(gx - 1, gy - u * 1.35, lv >= 5 ? 16 : 12, lv >= 5 ? 4 : 2.5).fill({ color: DARK, alpha });
      if (lv >= 3) g.rect(gx - 1, gy - u * 1.35 + 4, 12, 2.5).fill({ color: DARK, alpha });
      if (lv >= 2) g.rect(gx - 7, gy - u * 1.1, 4, 4).fill({ color: lv >= 4 ? 0xd23a2a : 0xf08a24, alpha });
      if (lv >= 5) g.circle(gx - 2, gy - u * 2.1, 3).stroke({ color: 0xd0d6dc, width: 1.5, alpha });
      break;
    }
    case 'howitzer': {
      // sandbagged pit, split-trail legs, gun shield and an elevated 155 mm tube with a muzzle brake
      const m = size / 2;
      cylinder(g, x + m, y + m, size * 9, u * 0.65, tint(SAND), alpha);
      cylinder(g, x + m, y + m, size * 6, u * 0.3, tint(CONCRETE), alpha, u * 0.2);
      const [gx, gy] = P(x + m, y + m);
      g.moveTo(gx - 14, gy - u * 0.3).lineTo(gx - 2, gy - u * 0.9).stroke({ color: tint(OLIVE), width: 3, alpha });
      g.moveTo(gx - 8, gy + 3 - u * 0.3).lineTo(gx - 1, gy - u * 0.9).stroke({ color: tint(OLIVE), width: 3, alpha });
      cylinder(g, x + m, y + m, size * 3, u * 0.9, tint(OLIVE), alpha, u * 0.5);
      g.moveTo(gx - 2, gy - u * 1.2).lineTo(gx + 20, gy - u * 2.5).stroke({ color: tint(OLIVE), width: 4, alpha });
      g.moveTo(gx + 17, gy - u * 2.35).lineTo(gx + 22, gy - u * 2.65).stroke({ color: DARK, width: 6, alpha });
      break;
    }
    case 'mortar': {
      // a 60 mm tube on its bipod behind a sandbag ring, steeper than the howitzer's barrel
      const [mx, my] = P(x + 0.5, y + 0.5);
      g.ellipse(mx, my, 13, 6.5).stroke({ color: tint(SAND), width: 4, alpha });
      g.moveTo(mx - 7, my - u * 0.45).lineTo(mx + 1, my - u * 1.6).stroke({ color: tint(DARK), width: 5, alpha });
      g.moveTo(mx + 0, my - u * 1.5).lineTo(mx - 3, my - u * 1.05).stroke({ color: tint(OLIVE), width: 2, alpha });
      g.moveTo(mx + 1, my - u * 1.6).lineTo(mx + 3, my - u * 1.0).stroke({ color: tint(OLIVE), width: 2, alpha });
      break;
    }
    case 'himars': {
      // an olive truck with the cab forward and a box pod of six rocket cells raised at the back
      const m = size / 2;
      const [hx, hy] = P(x + m, y + m);
      box(g, x + i0 + 0.25, y + 0.5, x + i1 - 0.15, y + i1 - 0.3, u * 0.55, tint(OLIVE), alpha);
      box(g, x + i0 + 0.25, y + 0.45, x + i0 + 0.85, y + i0 + 0.95, u * 0.75, tint(shade(OLIVE, 1.15)), alpha);
      box(g, x + i0 + 0.2, y + i0 + 0.15, x + i1 - 0.2, y + i1 - 0.35, u * 0.2, tint(0x3a4028), alpha);
      // the pod, tilted up
      g.poly([hx - 12, hy - u * 0.55, hx + 10, hy - u * 1.15, hx + 10, hy - u * 0.45, hx - 12, hy + 0]).fill({ color: tint(0x4a5236), alpha });
      for (let k = 0; k < 3; k++) {
        const px = hx - 8 + k * 6;
        g.circle(px + 1, hy - u * (0.8 + 0.25 * Math.cos(0.5)) - 2, 2).fill({ color: tint(DARK), alpha });
      }
      // the six black cell mouths
      for (let k = 0; k < 6; k++) {
        g.circle(hx - 9 + (k % 3) * 6.5, hy - u * 1.12 + Math.floor(k / 3) * 3, 2.2).fill({ color: tint(0x14161a), alpha });
      }
      break;
    }
    case 'avenger': {
      // a HMMWV with the Avenger turret mid-deck: two pods of four Stinger tubes
      const m = size / 2;
      const [ax, ay] = P(x + m, y + m);
      box(g, x + i0 + 0.2, y + i0 + 0.35, x + i1 - 0.15, y + i1 - 0.2, u * 0.5, tint(0x4a5d3a), alpha);
      box(g, x + i0 + 0.2, y + i0 + 0.3, x + i0 + 0.75, y + i0 + 0.85, u * 0.7, tint(shade(0x4a5d3a, 1.72)), alpha);
      for (const [sx0, sy0] of [[-6, -2], [6, -2]] as const) {
        box(g, x + m + sx0 / TILE_W, y + m + sy0 / TILE_W, x + m + sx0 / TILE_W + 0.32, y + m + sy0 / TILE_W + 0.34, u * 0.42, tint(0x394a2c), alpha);
      }
      g.rect(ax - 1, ay - u * 1.15, 9, 5).fill({ color: tint(DARK), alpha });
      // two racks of four tubes, forward over the cab roof
      for (const o of [-4, 4]) {
        for (let k = 0; k < 4; k++) {
          g.rect(ax + o - 1, ay - u * 1.3 - k * 2.5, 2.2, 2).fill({ color: tint(0x2b3a20), alpha });
        }
      }
      break;
    }
    case 'gepard': {
      // a tracked hull with the twin 35 mm Oerlikons and the search radar drum behind them
      const m = size / 2;
      const [px, py] = P(x + m, y + m);
      box(g, x + i0 + 0.3, y + i0 + 0.4, x + i1 - 0.25, y + i1 - 0.3, u * 0.6, tint(0x52634f), alpha);
      box(g, x + i0 + 0.42, y + i0 + 0.52, x + i1 - 0.37, y + i1 - 0.42, u * 0.28, tint(0x2f3830), alpha);
      cylinder(g, x + m, y + m, size * 4.5, u * 0.75, tint(0x5f705a), alpha, u * 0.55);
      g.moveTo(px - 3, py - u * 1.15).lineTo(px + 13, py - u * 1.75).stroke({ color: tint(DARK), width: 3, alpha });
      g.moveTo(px - 1, py - u * 1.05).lineTo(px + 15, py - u * 1.7).stroke({ color: tint(DARK), width: 3, alpha });
      g.ellipse(px - 11, py - u * 1.95, 6, 2.5).stroke({ color: tint(0xc8cfd6), width: 1.5, alpha });
      break;
    }
    case 'iron_beam': {
      // a white generator bin on skids, with the emitter turret on a stub mast
      const m = size / 2;
      const [bx, by] = P(x + m, y + m);
      box(g, x + i0 + 0.25, y + i0 + 0.25, x + i1 - 0.25, y + i1 - 0.25, u * 0.85, tint(0xc2c4c8), alpha);
      for (const s of boxSides(x + i0 + 0.25, y + i0 + 0.25, x + i1 - 0.25, y + i1 - 0.25)) {
        wallPanels(g, s.p, s.q, u * 0.85, 2, 1, shade(0x8e9198, s.light), alpha, c => c === 0);
      }
      cylinder(g, x + m, y + m, 2.4, u * 0.55, tint(0xb8bcc2), alpha, u * 0.85);
      g.circle(bx, by - u * 1.62, 4.5).fill({ color: tint(0x2f3b4a), alpha });
      g.circle(bx, by - u * 1.62, 1.8).fill({ color: tint(0xdf3a2e), alpha });
      break;
    }
    case 'c_ram': {
      // an armoured turret on a round base, with a radar dish on top; the barrels spin in drawBuildingFx
      const m = size / 2;
      // a steel base, a taller armoured turret on it, and a radar dish above the barrels
      cylinder(g, x + m, y + m, size * 11, u * 0.5, tint(0x6f777e), alpha);
      cylinder(g, x + m, y + m, size * 7, u * 1.6, tint(0xb8c0c7), alpha, u * 0.5);
      const [cx, cy] = P(x + m, y + m);
      g.ellipse(cx, cy - u * 3.6, 16, 6).stroke({ color: 0xe6edf2, width: 2, alpha });
      g.rect(cx - 0.5, cy - u * 3.6, 1.5, u * 1.4).fill({ color: 0xe6edf2, alpha });
      break;
    }
    case 'barrier': {
      // run to the tile edge wherever another barrier stands, so a row of them reads as one wall
      const joins = (dx: number, dy: number) => barriers?.has(`${x + dx},${y + dy}`) ?? false;
      box(g,
        joins(-1, 0) ? x : x + 0.04, joins(0, -1) ? y : y + 0.04,
        joins(1, 0) ? x + 1 : x + 0.96, joins(0, 1) ? y + 1 : y + 0.96,
        u * 0.75, tint(CONCRETE), alpha);
      break;
    }
    case 'electric_fence': {
      // two posts with wire between them, drawn in the cool colour of the charge
      const [p1x, p1y] = P(x + 0.5, y + 0.2), [p2x, p2y] = P(x + 0.5, y + 0.8);
      g.moveTo(p1x, p1y).lineTo(p2x, p2y).stroke({ color: tint(DARK), width: 3, alpha });
      g.rect(p1x - 1.5, p1y - u * 0.9, 3, u * 0.9).fill({ color: tint(DARK), alpha });
      g.rect(p2x - 1.5, p2y - u * 0.9, 3, u * 0.9).fill({ color: tint(DARK), alpha });
      g.moveTo(p1x, p1y - u * 0.6).lineTo(p2x, p2y - u * 0.6).stroke({ color: tint(0x6fd3ff), width: 1, alpha });
      break;
    }
    case 'land_mine': {
      // a flat, low disc: a mine is meant to be hard to see
      const [mx, my] = P(x + 0.5, y + 0.5);
      g.ellipse(mx, my, 6, 3).fill({ color: tint(0x5c5c3a), alpha });
      g.circle(mx, my - 1, 1.5).fill({ color: tint(0xd14b4b), alpha });
      break;
    }
    case 'oil_derrick': {
      pad(g, x + i0, y + i0, x + i1, y + i1, tint(0x3b3f45), alpha);
      const m = size / 2;
      const [bx, by] = P(x + m, y + m);
      const top = by - u * 4.2, w = TILE_W * 0.45;
      g.moveTo(bx - w, by).lineTo(bx, top).lineTo(bx + w, by).moveTo(bx - w * 0.6, by - u * 1.6).lineTo(bx + w * 0.6, by - u * 1.6)
        .moveTo(bx - w * 0.3, by - u * 3).lineTo(bx + w * 0.3, by - u * 3).stroke({ color: tint(0x3f8fd6), width: 2, alpha });
      if (fx) {
        const bob = Math.sin(t / 400 + seed) * 3;
        g.rect(bx - 10, top + 6 + bob, 20, 3).fill({ color: tint(DARK), alpha });
      }
      cylinder(g, x + 0.7, y + size - 0.7, 5, u * 0.8, tint(0x2a5f91), alpha);
      break;
    }
    case 'fishery': {
      // a concrete quay around a dark water basin, with a hull moored in it
      pad(g, x + i0, y + i0, x + i1, y + i1, tint(0x8c8a80), alpha);
      pad(g, x + 0.5, y + 0.5, x + size - 0.5, y + size - 0.5, tint(0x1d3b4c), alpha);
      const [hx, hy] = P(x + size / 2, y + size / 2);
      g.poly([hx - 12, hy, hx + 12, hy, hx + 8, hy + 4, hx - 8, hy + 4]).fill({ color: tint(0xc9bfa0), alpha });
      break;
    }
    case 'fuel_tank':
      cylinder(g, x + size / 2, y + size / 2, size * 8, u * 2.2, tint(0x3a6f9f), alpha);
      cylinder(g, x + size / 2, y + size / 2, size * 3, u * 0.25, tint(0x2a4f71), alpha, u * 2.2);
      break;
    case 'steel_mill': {
      box(g, x + i0, y + i0, x + i1, y + i1, u * 1.6, tint(STEEL), alpha);
      for (const s of boxSides(x + i0, y + i0, x + i1, y + i1)) {
        wallPanels(g, s.p, s.q, u * 1.6, 3, 1, shade(0x3a3f45, s.light), alpha, c => c !== 1);
      }
      cylinder(g, x + 0.8, y + 0.8, 4, u * 3.4, tint(RUST), alpha);
      cylinder(g, x + 1.6, y + 0.8, 4, u * 2.8, tint(RUST), alpha);
      if (fx) {
        const [cx, cy] = P(x + 0.8, y + 0.8);
        const drift = (t / 40 + seed * 13) % 30;
        g.circle(cx + drift * 0.4, cy - u * 3.4 - drift, 3 + drift * 0.12).fill({ color: 0xbbbbbb, alpha: 0.35 * (1 - drift / 30) * alpha });
      }
      break;
    }
    case 'steel_yard':
      pad(g, x + i0, y + i0, x + i1, y + i1, tint(0x45484c), alpha);
      // three stacks of plate, far ones first at any view angle
      for (const k of [0, 1, 2].sort((m, n) => depth(x + size / 2, y + 0.6 + m * 0.75) - depth(x + size / 2, y + 0.6 + n * 0.75))) {
        box(g, x + 0.4, y + 0.4 + k * 0.75, x + size - 0.4, y + 0.85 + k * 0.75, u * (0.9 + (k % 2) * 0.5), tint(shade(STEEL, 0.8 + k * 0.08)), alpha);
        // ribs across each plate stack
        for (const s of boxSides(x + 0.4, y + 0.4 + k * 0.75, x + size - 0.4, y + 0.85 + k * 0.75)) {
          wallPanels(g, s.p, s.q, u * (0.9 + (k % 2) * 0.5), 4, 1, shade(0x4a4f55, s.light), alpha, c => c % 2 === 1);
        }
      }
      break;
    case 'solar_array': {
      // a grey pad with two rows of blue panels on legs
      pad(g, x + i0, y + i0, x + i1, y + i1, tint(0x3d4650), alpha);
      for (const k of [0, 1]) box(g, x + 0.3, y + 0.3 + k * 0.85, x + size - 0.3, y + 0.75 + k * 0.85, u * 0.5, tint(0x1f4e8c), alpha);
      break;
    }
    case 'wind_turbine': {
      // a tall white mast with three blades; the blades turn with time
      const m = size / 2;
      cylinder(g, x + m, y + m, 3, u * 4.5, tint(0xe6ebf0), alpha);
      const [hx, hy] = P(x + m, y + m);
      const hub = hy - u * 4.5;
      const spin = fx ? t / 300 + seed : 0;
      for (let k = 0; k < 3; k++) {
        const a = spin + (k * 2 * Math.PI) / 3;
        g.moveTo(hx, hub).lineTo(hx + Math.cos(a) * 16, hub + Math.sin(a) * 16).stroke({ color: tint(0xf5f7fa), width: 3, alpha });
      }
      g.circle(hx, hub, 2.5).fill({ color: tint(DARK), alpha });
      break;
    }
    case 'nuclear_reactor': {
      // a concrete pad with two cooling towers and a dome
      pad(g, x + i0, y + i0, x + i1, y + i1, tint(0x55585c), alpha);
      cylinder(g, x + 1.1, y + 1.1, 5, u * 3.2, tint(0xd8d8d0), alpha);
      cylinder(g, x + 2.9, y + 2.9, 5, u * 3.2, tint(0xd8d8d0), alpha);
      cylinder(g, x + 2, y + 2, 4, u * 0.6, tint(0x9aa0a6), alpha, u * 0.1);
      break;
    }
    case 'supply_depot':
    {
      // crate piles, far ones first; the tall crate stands on the first pile, so it goes with it
      const piles: [number, number, () => void][] = [
        [0.6, 0.6, () => { box(g, x + 0.2, y + 0.2, x + 1.0, y + 1.0, u * 0.9, tint(0x9b7b4a), alpha); box(g, x + 0.45, y + 0.45, x + 0.95, y + 0.95, u * 1.6, tint(0xb08f58), alpha); }],
        [1.4, 0.7, () => box(g, x + 1.0, y + 0.3, x + 1.8, y + 1.1, u * 0.8, tint(0x8f7142), alpha)],
        [0.8, 1.4, () => box(g, x + 0.4, y + 1.0, x + 1.2, y + 1.8, u * 0.8, tint(0xa38550), alpha)],
      ];
      piles.sort((m, n) => depth(x + m[0], y + m[1]) - depth(x + n[0], y + n[1]));
      for (const [, , draw] of piles) draw();
    }
      break;
    case 'warehouse': {
      box(g, x + i0, y + i0, x + i1, y + i1, u * 1.4, tint(0x6f7a4d), alpha);
      const a = P(x + i0, y + size / 2), c = P(x + i1, y + size / 2);
      const a0 = P(x + i0, y + i0), d0 = P(x + i0, y + i1), b0 = P(x + i1, y + i0), c0 = P(x + i1, y + i1);
      const h = u * 1.4, r = u * 1.0;
      // the two roof slopes, the far one first, then the gable end facing the viewer
      const south = () => g.poly([d0[0], d0[1] - h, c0[0], c0[1] - h, c[0], c[1] - h - r, a[0], a[1] - h - r]).fill({ color: tint(0x56603a), alpha });
      const north = () => g.poly([a0[0], a0[1] - h, b0[0], b0[1] - h, c[0], c[1] - h - r, a[0], a[1] - h - r]).fill({ color: tint(0x7d8a56), alpha });
      if (depth(x + size / 2, y + i1) >= depth(x + size / 2, y + i0)) { north(); south(); } else { south(); north(); }
      if (a0[0] < d0[0]) g.poly([d0[0], d0[1] - h, a0[0], a0[1] - h, a[0], a[1] - h - r]).fill({ color: tint(0x5f6a42), alpha });
      if (c0[0] < b0[0]) g.poly([b0[0], b0[1] - h, c0[0], c0[1] - h, c[0], c[1] - h - r]).fill({ color: tint(0x5f6a42), alpha });
      // a roller door on the wall that faces the viewer, and small vents above it
      for (const s of boxSides(x + i0, y + i0, x + i1, y + i1)) {
        wallPanels(g, s.p, s.q, h, 2, 2, shade(0x3b4228, s.light), alpha, (cc, r) => r === 0 ? cc === 0 : true);
      }
      break;
    }
    case 'farm': {
      pad(g, x + i0, y + i0, x + i1, y + i1, tint(0x5a4a2c), alpha);
      // crop rows running across the plot, far ones first
      for (let k = 0; k < 4; k++) {
        const ry = y + 0.4 + k * 0.6;
        box(g, x + 0.3, ry, x + size - 0.3, ry + 0.2, u * 0.25, tint(shade(0x7fa650, 0.8 + (k % 2) * 0.2)), alpha);
      }
      cylinder(g, x + size - 0.6, y + 0.6, 4, u * 2.4, tint(0xc8c3b4), alpha);
      break;
    }
    case 'water_supply': {
      // a tank on four legs
      const m = size / 2;
      for (const [lx, ly] of [[0.3, 0.3], [size - 0.3, 0.3], [0.3, size - 0.3], [size - 0.3, size - 0.3]]) {
        cylinder(g, x + lx!, y + ly!, 1.2, u * 1.6, tint(STEEL), alpha);
      }
      cylinder(g, x + m, y + m, size * 3, u * 1.2, tint(0x4fb3c9), alpha, u * 1.6);
      break;
    }
    case 'staging_area': {
      pad(g, x + i0, y + i0, x + i1, y + i1, tint(0x4a4f3a), alpha);
      const tents = [[0.4, 0.4], [1.6, 0.6], [0.7, 1.6]].sort((m, n) => depth(x + m[0]! + 0.4, y + m[1]! + 0.4) - depth(x + n[0]! + 0.4, y + n[1]! + 0.4));
      for (const [tx, ty] of tents) pyramid(g, x + tx!, y + ty!, x + tx! + 0.8, y + ty! + 0.8, u * 1.3, tint(0x6b7a45), alpha);
      break;
    }
    default:
      box(g, x + i0, y + i0, x + i1, y + i1, size * u * 0.8, tint(COLORS[b.type] ?? 0xffffff), alpha);
  }

  if (state === 'selected') {
    const a = P(x, y), bb = P(x + size, y), c = P(x + size, y + size), d = P(x, y + size);
    g.poly([a[0], a[1], bb[0], bb[1], c[0], c[1], d[0], d[1]]).stroke({ color: 0xffffff, width: 2 });
  }

  // health bar when damaged or selected, smoke once below 60 percent
  if (!ghost && (b.hpFrac < 1 || state === 'selected')) {
    const [cx, cy] = P(x + size / 2, y + size / 2);
    const top = cy - size * u * 1.1 - 8;
    g.rect(cx - 10, top, 20, 3).fill({ color: 0x000000, alpha: 0.6 });
    g.rect(cx - 10, top, 20 * b.hpFrac, 3).fill({ color: b.hpFrac > 0.5 ? 0x7ee787 : b.hpFrac > 0.25 ? 0xe0b341 : 0xd14b4b });
    if (fx && b.hpFrac < 0.6) {
      for (let k = 0; k < 3; k++) {
        const phase = ((t / 900 + rand(seed, k)) % 1);
        g.circle(cx + (rand(seed, k + 5) - 0.5) * 14 + phase * 6, cy - u * 1.2 - phase * 26, 3 + phase * 6)
          .fill({ color: 0x444444, alpha: 0.5 * (1 - phase) });
      }
    }
  }
}

/**
 * Draws buildings back to front. `t` is a clock in ms for small animations.
 * With `fx` false only the still parts are drawn, so the result can be kept
 * until a building changes; drawBuildingFx then animates the rest cheaply.
 */
export function drawBuildings(g: Graphics, list: readonly DrawableBuilding[], t = performance.now(), fx = true): void {
  g.clear();
  const mid = (b: DrawableBuilding) => depth(b.x + b.size / 2, b.y + b.size / 2);
  const sorted = [...list].sort((a, b) => mid(a) - mid(b));
  const barriers = new Set(list.filter(b => b.type === 'barrier').map(b => `${b.x},${b.y}`));
  for (const b of sorted) drawOne(g, b, t, fx, barriers);
}

/**
 * The building layer, which the 3D look replaces: in 3D the list goes to the scene
 * instead and this layer stays empty. Ghosts keep using drawBuildings directly.
 */
export function paintBuildings(g: Graphics, list: readonly DrawableBuilding[], t = performance.now(), fx = true): void {
  if (is3D()) { setBuildings3D(list); g.clear(); return; }
  drawBuildings(g, list, t, fx);
}

/** Same for the animated building effects, which the 3D look does not draw. */
export function paintBuildingFx(g: Graphics, list: readonly DrawableBuilding[], t = performance.now(), alarmAt = -1): void {
  if (is3D()) { setBuildingFx3D(list); g.clear(); return; }
  drawBuildingFx(g, list, t, alarmAt);
}

/** The ground grid, which the 3D ground replaces. */
export function paintGrid(g: Graphics, shadeNoDeploy?: Uint8Array, claim?: ClaimRect | null): void {
  if (is3D()) { g.clear(); return; }
  drawGrid(g, shadeNoDeploy, claim);
}

/**
 * Staff walking a loop round a running building, so it looks worked. Two
 * people per building, one for a water supply. Drawn in the animated layer.
 */
/** How long the staff take to run for cover once the alarm rings. */
const RUN_MS = 2500;

/**
 * A C-RAM's six barrels turning on their hub, faster while it fires, with a muzzle flash
 * at a barrel tip on each shot. Idle it turns slowly, so it always looks armed.
 */
function drawRamBarrels(g: Graphics, b: DrawableBuilding, t: number): void {
  const u = TILE_H;
  const [cx, cy] = P(b.x + b.size / 2, b.y + b.size / 2);
  const hubY = cy - u * 2.0;
  const seed = b.seed ?? (b.x * 97 + b.y * 31);
  const speed = b.firing ? 0.045 : 0.004;
  const angle = t * speed + seed;
  for (let k = 0; k < 6; k++) {
    const a = angle + (k * Math.PI) / 3;
    const bx = cx + Math.cos(a) * 3.2, by = hubY + Math.sin(a) * 1.6;
    const tipX = bx + Math.cos(a) * 11, tipY = by + Math.sin(a) * 5.5;
    g.moveTo(bx, by).lineTo(tipX, tipY).stroke({ color: 0x2b2f33, width: 2.2 });
    if (b.firing && Math.sin(a) > 0) {
      // the barrel pointing toward the viewer flashes as it fires
      g.circle(tipX, tipY, 2.5 + Math.random() * 2).fill({ color: 0xffd27a, alpha: 0.85 });
    }
  }
  g.circle(cx, hubY, 2).fill({ color: DARK });
}

function drawWorkers(g: Graphics, b: DrawableBuilding, t: number, alarmAt: number, cover: readonly DrawableBuilding[]): void {
  const { x, y, size } = b;
  const seed = b.seed ?? (x * 97 + y * 31);
  const lo = x - 0.5, hi = x + size + 0.5;
  const corners: [number, number][] = [[lo, lo], [hi, lo], [hi, hi], [lo, hi]];
  const count = b.type === 'water_supply' ? 1 : 2;
  // the Command Center: where everyone runs when the alarm sounds
  const refuge = nearestCover(x + size / 2, y + size / 2, cover);
  for (let k = 0; k < count; k++) {
    // one lap every few seconds, each worker starting at its own point on the loop
    const d = ((t / 6000 + rand(seed, 40 + k)) % 1) * 4;
    const side = Math.floor(d), f = d - side;
    const [ax, ay] = corners[side]!, [bx, by] = corners[(side + 1) % 4]!;
    let [sx, sy] = P(ax + (bx - ax) * f, ay + (by - ay) * f);
    let running = false;
    if (alarmAt >= 0 && refuge) {
      // the alarm: each worker sets off a moment after the others and runs for the refuge
      const p = Math.min(1, Math.max(0, (t - alarmAt - rand(seed, 60 + k) * 800) / RUN_MS));
      if (p >= 1) continue;
      const [tx, ty] = P(refuge.x + refuge.size / 2, refuge.y + refuge.size / 2);
      const e = p * p * (3 - 2 * p);
      sx += (tx - sx) * e;
      sy += (ty - sy) * e;
      running = p > 0;
    }
    const bob = running ? Math.abs(Math.sin(t / 60 + k * 2)) * 2.5 : Math.abs(Math.sin(t / 150 + k * 2)) * 1.2;
    g.rect(sx - 2, sy - 8 - bob, 4, 5).fill({ color: 0xe0803b });
    g.circle(sx, sy - 10 - bob, 1.8).fill({ color: 0xd9b38c });
    g.rect(sx - 1.5, sy - 3, 3, 3).fill({ color: 0x33363b });
  }
}

/** Just the animated bits: flags, pumps, chimney smoke and damage smoke. */
/** The nearest standing Command Center to a point, the one place the staff run for. */
function nearestCover(cx: number, cy: number, list: readonly DrawableBuilding[]): DrawableBuilding | undefined {
  const cover = list.filter(b => b.type === 'hq' && b.state !== 'dead');
  let best: DrawableBuilding | undefined;
  let bestD = Infinity;
  for (const b of cover) {
    const d = (b.x + b.size / 2 - cx) ** 2 + (b.y + b.size / 2 - cy) ** 2;
    if (d < bestD) { bestD = d; best = b; }
  }
  return best;
}

/**
 * The animated layer. `alarmAt` is when the raid's alarm rang (performance.now time), or -1
 * for none: from then on the workers run to the Command Center and vanish.
 */
export function drawBuildingFx(g: Graphics, list: readonly DrawableBuilding[], t = performance.now(), alarmAt = -1): void {
  g.clear();
  const u = TILE_H;
  for (const b of list) {
    if (b.state === 'dead' || b.state === 'ghost-ok' || b.state === 'ghost-bad') continue;
    const { x, y, size } = b;
    const seed = b.seed ?? (x * 97 + y * 31);
    if (b.disabled) {
      const [dx, dy] = P(x + size / 2, y + size / 2);
      g.ellipse(dx, dy, size * 9, size * 4.5).stroke({ color: EMP_COLOR, width: 1.2, alpha: 0.45 + 0.4 * Math.sin(t / 70) });
    }
    if (b.type === 'hq') {
      const [ax, ay] = P(x + size - 1.1, y + 1.1);
      const wave = Math.sin(t / 300) * 1.5;
      g.poly([ax + 0.5, ay - u * 4.4, ax + 9, ay - u * 4.1 + wave, ax + 0.5, ay - u * 3.8]).fill({ color: FLAG });
    } else if (b.type === 'oil_derrick') {
      const [bx, by] = P(x + size / 2, y + size / 2);
      const bob = Math.sin(t / 400 + seed) * 3;
      g.rect(bx - 10, by - u * 4.2 + 6 + bob, 20, 3).fill({ color: DARK });
    } else if (b.type === 'steel_mill') {
      const [cx, cy] = P(x + 0.8, y + 0.8);
      const drift = (t / 40 + seed * 13) % 30;
      g.circle(cx + drift * 0.4, cy - u * 3.4 - drift, 3 + drift * 0.12).fill({ color: 0xbbbbbb, alpha: 0.35 * (1 - drift / 30) });
    }
    if (WORKER_TYPES.has(b.type)) drawWorkers(g, b, t, alarmAt, list);
    if (b.type === 'c_ram') drawRamBarrels(g, b, t);
    if (b.hpFrac < 0.6) {
      const [cx, cy] = P(x + size / 2, y + size / 2);
      for (let k = 0; k < 3; k++) {
        const phase = ((t / 900 + rand(seed, k)) % 1);
        g.circle(cx + (rand(seed, k + 5) - 0.5) * 14 + phase * 6, cy - u * 1.2 - phase * 26, 3 + phase * 6)
          .fill({ color: 0x444444, alpha: 0.5 * (1 - phase) });
      }
    }
  }
}

/** Where a label for a building should sit: just above its roof, in world pixels. */
export function buildingTop(b: { x: number; y: number; size: number }): [number, number] {
  const [cx, cy] = toScreen(b.x + b.size / 2, b.y + b.size / 2);
  return [cx, cy - b.size * TILE_H * 1.1 - 12];
}

/** Where a label for a unit at sim position (x, y) should sit, in world pixels. */
export function unitTop(x: number, y: number): [number, number] {
  const [sx, sy] = unitsToScreen(x, y);
  return [sx, sy - 18];
}

/** Changes whenever anything drawn by the still layer would change. */
export function buildingsSignature(list: readonly DrawableBuilding[]): string {
  let s = '';
  for (const b of list) s += `${b.x},${b.y},${b.level ?? 0},${b.state ?? ''},${Math.round(b.hpFrac * 40)};`;
  return s;
}

/** Level badges for the editor. */
export function drawLevels(layer: Container, list: readonly DrawableBuilding[]): void {
  layer.removeChildren();
  for (const b of list) {
    // the Command Center has no levels, so it carries no badge
    if (b.type === 'barrier' || b.type === 'hq' || b.level === undefined || b.level < 2) continue;
    const [cx, cy] = toScreen(b.x + b.size / 2, b.y + b.size / 2);
    const t = new Text({ text: `L${b.level}`, style: { fontSize: 9, fill: 0xffffff, fontFamily: 'system-ui', fontWeight: '700' } });
    t.anchor.set(0.5);
    t.position.set(cx, cy + 6);
    layer.addChild(t);
  }
}

// ---- units -------------------------------------------------------------------------

/**
 * How each soldier type looks. `helmet` is null for the bareheaded conscript, `wide` scales the
 * shoulders, `scope` adds a sight to the rifle and `pack` a backpack on the heavy gunner.
 */
const UNIT_LOOK: Record<string, { body: number; r: number; gun: number; wide: number; helmet: number | null; scope?: boolean; pack?: boolean }> = {
  rifleman: { body: 0x4f5a3c, r: 4.2, gun: 6, wide: 1, helmet: 0x2a2f24 },
  conscript: { body: 0x7d8a6e, r: 3.9, gun: 5, wide: 0.9, helmet: null },
  marksman: { body: 0x4f6b4a, r: 4.0, gun: 11, wide: 0.9, helmet: 0x3a4a33, scope: true },
  heavy_gunner: { body: 0x3f6b3a, r: 5.6, gun: 8, wide: 1.5, helmet: 0x2c3e24, pack: true },
  scout_drone: { body: 0x9ad0ff, r: 3.4, gun: 4, wide: 0.8, helmet: null },
  attack_drone: { body: 0x5a5f66, r: 3.8, gun: 4, wide: 0.8, helmet: null },
  attack_heli: { body: 0x3f6f3f, r: 6, gun: 9, wide: 1.2, helmet: null },
  predator: { body: 0xd8dde4, r: 4.6, gun: 5, wide: 1.6, helmet: null },
  cruise_missile: { body: 0xe8e8e8, r: 3.6, gun: 6, wide: 0.6, helmet: null },
  kalibr: { body: 0xd8dcd8, r: 3.6, gun: 6, wide: 0.6, helmet: null },
  tank: { body: 0x6b7a45, r: 4, gun: 9, wide: 1.4, helmet: null },
  car_bomb: { body: 0xc8b48a, r: 3.6, gun: 0, wide: 1.1, helmet: null },
  a10: { body: 0x7d868c, r: 5.5, gun: 6, wide: 1.6, helmet: null },
  kamikaze: { body: 0x8a9a6a, r: 5, gun: 4, wide: 1.4, helmet: null },
  boat: { body: 0xc9d6df, r: 3.2, gun: 4, wide: 1, helmet: null },
  destroyer: { body: 0x6f7f8c, r: 6, gun: 9, wide: 1.4, helmet: null },
};
const SKIN = 0xb98b68;
const TROUSERS = 0x2a2d31;

/** Units that fly. Drawn raised above the ground, so they read as airborne. */
export const AIRBORNE = new Set(['a10', 'kamikaze', 'scout_drone', 'attack_drone', 'fpv_drone', 'shahed', 'predator', 'attack_heli', 'strike_jet', 'f16', 'cruise_missile', 'kalibr', 'ballistic', 'hgv', 'hgv_nuclear', 'icbm', 'emp_missile', 'interceptor']);
/** Fast jets, flying higher than the helicopters. */
export const JETS = new Set(['strike_jet', 'f16', 'a10']);
/** Drones, drawn as one family. */
export const DRONES = new Set(['scout_drone', 'attack_drone', 'fpv_drone', 'shahed', 'predator']);
/** Missiles, drawn as one: the cruise missile flying in and the warheads coming down. */
export const MISSILES = new Set(['cruise_missile', 'kalibr', 'ballistic', 'hgv', 'hgv_nuclear', 'icbm', 'emp_missile', 'interceptor']);

/** Ships. Drawn as hulls on the water, not as soldiers. */
const SHIPS = new Set(['boat', 'destroyer', 'landing_craft', 'submarine', 'carrier']);

/** Ground vehicles: a tank's hull, turret and barrel. */
const VEHICLES = new Set(['tank', 'car_bomb']);
/** Propeller aircraft, drawn as aircraft and flown low. */
export const PROP_PLANES = new Set(['kamikaze']);
/** Defenders' roaming units are tinted blue so they read apart from the attackers. */
const DEFENDER_BODY = 0x3f6fa0;

/** A thin dark edge for a shape, so units read against the ground. */
const EDGE = { color: 0x14171a, width: 0.7, alpha: 0.55 };

/** Units are drawn this much larger than their base size: at game zoom the originals were too small to read. */
const UNIT_SCALE = 1.5;

/** A small set of points along a wedge: the hull or turret outline, in local units (u forward, v down), from the unit's centre. */
function local(sx: number, sy: number, dir: number, s: number): (u: number, v: number) => [number, number] {
  return (u, v) => [sx + dir * u * s, sy + v * s];
}

/** A tank: a sloped hull on tracks, a turret with a rear bustle and a hatch, a long barrel with a muzzle brake, and side skirts. */
function drawTank(g: Graphics, sx: number, sy: number, look: { body: number; r: number; gun: number }, dir: number): void {
  const r = look.r;
  const P = local(sx, sy, dir, r);
  g.ellipse(sx, sy + 1, r * 2.4, r * 0.8).fill({ color: 0x000000, alpha: 0.38 });
  // tracks: dark belts with a row of road wheels and a drive sprocket at the front
  for (const side of [-1, 1]) {
    const ty = sy - r * 0.2 + side * r * 0.5;
    g.roundRect(sx - r * 2.0, ty - r * 0.3, r * 4.0, r * 0.6, r * 0.3).fill({ color: 0x191c20 }).stroke(EDGE);
    for (let k = 0; k < 5; k++) g.circle(sx - r * 1.6 + k * r * 0.8, ty, r * 0.19).fill({ color: 0x4c535b }).stroke({ color: 0x0c0e10, width: 0.5, alpha: 0.6 });
    g.circle(sx + dir * r * 1.85, ty, r * 0.3).fill({ color: 0x343a41 });
  }
  // hull: a low body with a sloped glacis at the front, lit on top and shaded at the bottom
  const hull = [P(-1.7, -0.85), P(1.2, -0.85), P(1.85, -0.25), P(1.85, 0.15), P(-1.7, 0.15)];
  g.poly(hull.flat()).fill({ color: shade(look.body, 0.95) }).stroke(EDGE);
  g.poly([P(-1.7, -0.85), P(1.2, -0.85), P(1.85, -0.25), P(-0.2, -0.25)].flat()).fill({ color: shade(look.body, 1.12), alpha: 0.7 });
  g.poly([P(-1.7, 0.02), P(1.85, 0.02), P(1.85, 0.15), P(-1.7, 0.15)].flat()).fill({ color: shade(look.body, 0.55) });
  // turret: a rounded box with a bustle at the back, a hatch and a periscope
  // turret: a box with a sloped front, a lit top plane and a shaded side
  const tp = local(sx, sy, dir, r);
  g.poly([...tp(-0.95, -1.5), ...tp(0.55, -1.5), ...tp(0.98, -1.2), ...tp(0.86, -0.82), ...tp(-0.9, -0.82), ...tp(-0.98, -1.2)]).fill({ color: shade(look.body, 1.02) }).stroke(EDGE);
  g.poly([...tp(-0.95, -1.5), ...tp(0.55, -1.5), ...tp(0.3, -1.35), ...tp(-0.8, -1.35)]).fill({ color: shade(look.body, 1.2), alpha: 0.8 });
  g.poly([...tp(-0.9, -0.95), ...tp(0.86, -0.95), ...tp(0.86, -0.82), ...tp(-0.9, -0.82)]).fill({ color: shade(look.body, 0.6), alpha: 0.7 });
  g.roundRect(sx - dir * r * 1.0 - r * 0.45, sy - r * 1.42, r * 0.6, r * 0.6, r * 0.12).fill({ color: shade(look.body, 0.75) }).stroke(EDGE);
  g.circle(sx - dir * r * 0.2, sy - r * 1.6, r * 0.22).fill({ color: shade(look.body, 1.2) }).stroke(EDGE);
  g.rect(sx + dir * r * 0.25, sy - r * 1.75, 1, r * 0.2).fill({ color: DARK });
  // barrel with a muzzle brake, and the camouflage on the turret
  const bx0 = sx + dir * r * 0.6, bx1 = sx + dir * (r * 0.6 + look.gun * 1.0);
  g.rect(Math.min(bx0, bx1), sy - r * 1.22, Math.abs(bx1 - bx0), r * 0.22).fill({ color: 0x2b2f33 }).stroke(EDGE);
  g.rect(bx1 - (dir > 0 ? r * 0.18 : 0), sy - r * 1.3, r * 0.18, r * 0.38).fill({ color: 0x1d2024 });
  for (let k = 0; k < 3; k++) g.circle(sx - r * 0.6 + k * r * 0.5, sy - r * 1.3 + (k % 2) * r * 0.25, r * 0.12).fill({ color: shade(look.body, 0.65), alpha: 0.8 });
  // headlamp on the glacis, rivets along the hull, a spare track on the rear and an aerial
  g.circle(sx + dir * r * 1.75, sy - r * 0.35, r * 0.16).fill({ color: 0xfff2b0, alpha: 0.95 });
  for (let k = 0; k < 6; k++) g.circle(sx - r * 1.5 + k * r * 0.5, sy - r * 0.5, r * 0.05).fill({ color: 0x1d2024, alpha: 0.7 });
  g.roundRect(sx - dir * r * 1.9 - r * 0.2, sy - r * 0.95, r * 0.4, r * 0.7, r * 0.1).fill({ color: 0x2c3136 }).stroke(EDGE);
  g.moveTo(sx - dir * r * 0.9, sy - r * 1.55).lineTo(sx - dir * r * 1.3, sy - r * 2.7).stroke({ color: DARK, width: 0.8 });
}

/** Samples a quadratic curve from p0 to p2 bent by control point p1, in local (forward, up) units. */
function curve(p0: [number, number], p1: [number, number], p2: [number, number], n: number): [number, number][] {
  const out: [number, number][] = [];
  for (let k = 0; k <= n; k++) {
    const t = k / n, u = 1 - t;
    out.push([u * u * p0[0] + 2 * u * t * p1[0] + t * t * p2[0], u * u * p0[1] + 2 * u * t * p1[1] + t * t * p2[1]]);
  }
  return out;
}

/**
 * A ship: a low hull with a raked, curved bow and a squared stern, a lit deck over a shaded side,
 * a sloped bridge with a lit window band, a radar mast, a gun, and a wake. A destroyer is longer
 * and carries funnels and a second turret.
 */
function drawHull(g: Graphics, sx: number, sy: number, look: { body: number; r: number; gun: number }, dir: number, now: number, destroyer: boolean): void {
  const r = look.r;
  const P = local(sx, sy, dir, r);
  const L = destroyer ? 2.3 : 1.7;
  const pts = (list: [number, number][]) => list.flatMap(([u, v]) => P(u, v));
  // shadow and foam: the water is pushed out at the bow and tails off astern
  g.ellipse(sx, sy + 1, r * (L + 0.5), r * 0.7).fill({ color: 0x0b1820, alpha: 0.45 });
  const wake = (Math.sin(now / 300) * 0.5 + 0.5) * 0.25;
  for (const k of [0.1, 0.45]) g.moveTo(...P(-L * 0.7, k)).lineTo(...P(-L - 0.9, k + 0.18)).stroke({ color: 0xeaf6ff, width: 0.8, alpha: 0.06 + wake * 0.4 });
  // the hull outline: stern, a flat keel, the raked bow curving up to the stem, and the deck back to the stern
  const bow = curve([L * 0.4, 0.45], [L + 0.1, 0.4], [L + 0.45, -0.15], 8);
  const hull: [number, number][] = [[-L, -0.42], [L * 0.45, -0.42], ...bow.slice(1), [L * 0.2, 0.5], [-L * 0.9, 0.5]];
  g.poly(pts(hull)).fill({ color: shade(look.body, 0.72) }).stroke(EDGE);
  // the lit deck over the top of the hull, and a highlight along the bow curve
  g.poly(pts([[-L, -0.42], [L * 0.45, -0.42], [L * 0.5, -0.3], [-L, -0.3]])).fill({ color: shade(look.body, 1.15), alpha: 0.9 });
  g.moveTo(...P(L * 0.45, -0.42)).lineTo(...P(L + 0.45, -0.15)).stroke({ color: 0xffffff, width: 0.8, alpha: 0.25 });
  // the red boot-stripe at the waterline and a rubbing strake above it
  g.poly(pts([[-L * 0.9, 0.24], [L * 0.8, 0.24], [L * 0.8, 0.36], [-L * 0.9, 0.36]])).fill({ color: 0x8e2b2b });
  g.poly(pts([[-L * 0.95, -0.05], [L * 0.6, -0.05], [L * 0.62, 0.0], [-L * 0.95, 0.0]])).fill({ color: 0x0f1316, alpha: 0.5 });
  // the bridge: a sloped front, a lit window band, and a door
  const bu = destroyer ? -0.15 : 0.05;
  g.poly(pts([[bu - 0.55, -0.42], [bu + 0.5, -0.42], [bu + 0.38, -1.25], [bu - 0.4, -1.25]])).fill({ color: look.body }).stroke(EDGE);
  g.poly(pts([[bu - 0.4, -1.25], [bu + 0.38, -1.25], [bu + 0.36, -1.1], [bu - 0.38, -1.1]])).fill({ color: 0x9fdcff, alpha: 0.85 });
  g.poly(pts([[bu - 0.32, -0.95], [bu + 0.32, -0.95], [bu + 0.3, -0.82], [bu - 0.3, -0.82]])).fill({ color: 0x9fdcff, alpha: 0.5 });
  if (destroyer) {
    // two funnels with red bands, and a second turret astern of the bridge
    for (const f of [-0.9, -1.55]) {
      g.poly(pts([[f - 0.14, -0.42], [f + 0.14, -0.42], [f + 0.12, -1.45], [f - 0.12, -1.45]])).fill({ color: 0x3a3f45 }).stroke(EDGE);
      g.poly(pts([[f - 0.14, -1.2], [f + 0.14, -1.2], [f + 0.13, -1.45], [f - 0.13, -1.45]])).fill({ color: 0xc23c2e });
    }
    g.circle(...P(1.3, -0.5), r * 0.26).fill({ color: 0x2b2f33 }).stroke(EDGE);
    g.rect(...P(1.3, -0.56), dir * r * 0.9, r * 0.13).fill({ color: 0x2b2f33 });
  } else {
    // a small forward gun on a round mount, with a barrel
    g.circle(...P(1.0, -0.42), r * 0.22).fill({ color: 0x2b2f33 }).stroke(EDGE);
    g.rect(...P(1.0, -0.47), dir * r * 0.8, r * 0.12).fill({ color: 0x2b2f33 });
  }
  // a lifebuoy on the bridge side, and fenders hanging off the hull
  g.circle(...P(bu + 0.55, -0.7), r * 0.18).fill({ color: 0xf2f2f2 }).stroke(EDGE);
  g.circle(...P(bu + 0.55, -0.7), r * 0.08).fill({ color: 0xd94a3a });
  for (const f of [-0.6, 0.5]) g.rect(...P(f * L - 0.06, 0.42), r * 0.12, r * 0.32).fill({ color: 0x3a3f45 });
  // a radar mast with a dish and a pennant
  const mastU = bu - 0.1;
  g.rect(...P(mastU, -2.85), 1, r * 1.6).fill({ color: DARK });
  g.ellipse(...P(mastU, -2.85), r * 0.35, r * 0.13).fill({ color: 0xc9d2d8 }).stroke(EDGE);
  g.poly(pts([[mastU, -2.4], [mastU + 0.5, -2.2], [mastU, -2.0]])).fill({ color: 0xd94a3a });
}

/** A helicopter: a bubble canopy, a tail boom with a fin, skids, and a main rotor that spins. */
function drawHeli(g: Graphics, sx: number, sy: number, look: { body: number; r: number }, dir: number, now: number): void {
  const r = look.r;
  // the rotor disc, spinning: a blurred ellipse with two blades
  const spin = now / 60;
  g.ellipse(sx, sy - r * 2.3, r * 2.6, r * 0.5).fill({ color: 0xdfe6ea, alpha: 0.18 });
  for (let k = 0; k < 2; k++) {
    const a = spin + k * Math.PI;
    g.moveTo(sx, sy - r * 2.3).lineTo(sx + Math.cos(a) * r * 2.6, sy - r * 2.3 + Math.sin(a) * r * 0.5).stroke({ color: 0x2b2f33, width: 1.4 });
  }
  // tail boom and tail fin
  g.poly([sx - dir * r * 0.6, sy - r * 1.4, sx - dir * r * 2.6, sy - r * 1.25, sx - dir * r * 2.6, sy - r * 1.0, sx - dir * r * 0.6, sy - r * 0.9]).fill({ color: shade(look.body, 0.8) }).stroke(EDGE);
  g.poly([sx - dir * r * 2.5, sy - r * 1.3, sx - dir * r * 2.9, sy - r * 2.0, sx - dir * r * 2.25, sy - r * 1.3]).fill({ color: look.body }).stroke(EDGE);
  // fuselage: a teardrop with a rounded nose, a tapered tail, a lit top and a shaded belly
  const hp = local(sx, sy, dir, r);
  const nose = curve([1.45, -1.25], [1.6, -1.95], [0.2, -2.0], 8);
  const back = curve([0.2, -2.0], [-0.9, -1.95], [-1.5, -1.35], 6);
  const belly = curve([-1.5, -1.35], [-0.8, -0.55], [0.9, -0.5], 6);
  const front = curve([0.9, -0.5], [1.4, -0.6], [1.45, -1.25], 4);
  const body = [...nose, ...back.slice(1), ...belly.slice(1), ...front.slice(1)];
  g.poly(body.flatMap(([u, v]) => hp(u, v))).fill({ color: look.body }).stroke(EDGE);
  g.poly(belly.concat(front).flatMap(([u, v]) => hp(u, v))).fill({ color: shade(look.body, 0.6), alpha: 0.7 });
  g.poly(nose.flatMap(([u, v]) => hp(u, v * 0.6 - 0.4))).fill({ color: shade(look.body, 1.2), alpha: 0.35 });
  // a faceted glass canopy with a reflection stripe
  g.poly([...hp(0.55, -1.9), ...hp(1.4, -1.5), ...hp(1.3, -1.0), ...hp(0.45, -1.0)]).fill({ color: 0x8fd3f0, alpha: 0.9 }).stroke(EDGE);
  g.poly([...hp(0.7, -1.8), ...hp(1.05, -1.6), ...hp(0.95, -1.45), ...hp(0.65, -1.6)]).fill({ color: 0xffffff, alpha: 0.35 });
  // skids, and a stub weapon pylon
  g.rect(sx - r * 1.2, sy - r * 0.2, r * 2.4, 1.1).fill({ color: 0x2b2f33 });
  g.rect(sx - r * 0.7, sy - r * 0.45, 1, r * 0.3).fill({ color: 0x2b2f33 });
  g.rect(sx + r * 0.6, sy - r * 0.45, 1, r * 0.3).fill({ color: 0x2b2f33 });
  g.rect(sx - dir * r * 0.2, sy - r * 0.8, dir * r * 1.1, 1.4).fill({ color: DARK });
  // rocket pods on stub wings, and a sensor ball under the nose
  for (const side of [-1, 1]) {
    g.roundRect(sx - r * 0.9, sy - r * 0.95 + side * 0.0, r * 1.8, r * 0.28, r * 0.12).fill({ color: 0x4d5258 }).stroke(EDGE);
    for (let k = 0; k < 3; k++) g.circle(sx - r * 0.7 + k * r * 0.55, sy - r * 0.81, r * 0.09).fill({ color: 0x2b2f33 });
  }
  g.circle(sx + dir * r * 1.1, sy - r * 0.7, r * 0.2).fill({ color: 0x2b2f33 }).stroke(EDGE);
  g.circle(sx + dir * r * 1.13, sy - r * 0.72, r * 0.07).fill({ color: 0xff5a4a });
}

/** A quadcopter drone: a central body, four arms with spinning rotors, and a camera or a payload. */
function drawDrone(g: Graphics, sx: number, sy: number, look: { body: number; r: number }, dir: number, now: number, armed: boolean): void {
  const r = look.r;
  const corners: [number, number][] = [[-1, -1], [1, -1], [1, 1], [-1, 1]];
  for (const [cx, cy] of corners) {
    const ax = sx + cx * r * 1.7, ay = sy - r * 0.9 + cy * r * 0.55;
    g.moveTo(sx, sy - r * 0.9).lineTo(ax, ay).stroke({ color: 0x2b2f33, width: 1.2 });
    // a spinning rotor disc
    g.ellipse(ax, ay, r * 0.75, r * 0.22).fill({ color: 0xdfe6ea, alpha: 0.22 });
    const a = now / 45 + cx * cy;
    g.moveTo(ax - Math.cos(a) * r * 0.7, ay - Math.sin(a) * r * 0.2).lineTo(ax + Math.cos(a) * r * 0.7, ay + Math.sin(a) * r * 0.2).stroke({ color: 0x1d2024, width: 1 });
  }
  g.ellipse(sx, sy + 1, r * 1.2, r * 0.45).fill({ color: 0x000000, alpha: 0.3 });
  // body: a rounded block lit on top, a glowing lens, and a missile pod when armed
  // a faceted shell: a dark lower half, a lit top plate, and chamfered corners
  g.poly([sx - r * 0.95, sy - r * 0.9, sx - r * 0.6, sy - r * 1.4, sx + r * 0.6, sy - r * 1.4, sx + r * 0.95, sy - r * 0.9, sx + r * 0.7, sy - r * 0.3, sx - r * 0.7, sy - r * 0.3])
    .fill({ color: look.body }).stroke(EDGE);
  g.poly([sx - r * 0.6, sy - r * 1.4, sx + r * 0.6, sy - r * 1.4, sx + r * 0.3, sy - r * 1.15, sx - r * 0.3, sy - r * 1.15]).fill({ color: shade(look.body, 1.25) });
  // landing legs under the body
  for (const lx of [-0.6, 0.6]) g.moveTo(sx + lx * r, sy - r * 0.3).lineTo(sx + lx * r * 1.1, sy + r * 0.15).stroke({ color: 0x2b2f33, width: 1.2 });
  g.circle(sx + dir * r * 0.5, sy - r * 0.85, r * 0.28).fill({ color: 0x9fdcff }).stroke(EDGE);
  // a gimbal camera hanging under the body, and status lights on the arms
  g.roundRect(sx - r * 0.3, sy - r * 0.35, r * 0.6, r * 0.35, r * 0.12).fill({ color: 0x2b2f33 }).stroke(EDGE);
  g.circle(sx, sy - r * 0.18, r * 0.12).fill({ color: 0x7ad0ff, alpha: 0.9 });
  for (const [cx, cy] of corners) g.circle(sx + cx * r * 1.7, sy - r * 0.9 + cy * r * 0.55, r * 0.07).fill({ color: cy < 0 ? 0x7ad0ff : 0xff7a5a });
  if (armed) {
    g.roundRect(sx - r * 0.5, sy - r * 0.5, r * 1.0, r * 0.3, r * 0.1).fill({ color: 0x4d5258 }).stroke(EDGE);
    g.circle(sx + dir * r * 0.6, sy - r * 0.35, r * 0.12).fill({ color: 0xffb040 });
  }
}

/** A cruise missile: an ogive nose, a two-tone body with a tapered boat-tail, swept wings, cruciform tail fins, an intake and a glowing nozzle. */
function drawMissile(g: Graphics, sx: number, sy: number, look: { body: number; r: number }, dir: number, now: number): void {
  const r = look.r;
  const P = local(sx, sy, dir, r);
  g.ellipse(sx, sy + 1, r * 2.8, r * 0.45).fill({ color: 0x000000, alpha: 0.3 });
  // exhaust: a flame cone out of the nozzle, flickering
  const flick = 0.75 + 0.25 * Math.sin(now / 35);
  g.poly([...P(-2.6, -0.25), ...P(-3.6 - 0.8 * flick, -0.1), ...P(-3.6 - 0.8 * flick, 0.1), ...P(-2.6, 0.2)]).fill({ color: 0xffb040, alpha: 0.9 });
  g.poly([...P(-2.6, -0.12), ...P(-3.1 - 0.5 * flick, -0.03), ...P(-2.6, 0.06)]).fill({ color: 0xfff1c0, alpha: 0.95 });
  // swept wings and cruciform tail fins: shaded underneath, lit on top
  g.poly([...P(-0.2, -0.4), ...P(0.9, -1.9), ...P(1.4, -1.9), ...P(0.5, -0.4)]).fill({ color: shade(look.body, 0.85) }).stroke(EDGE);
  g.poly([...P(-0.2, 0.4), ...P(0.9, 1.5), ...P(1.4, 1.5), ...P(0.5, 0.4)]).fill({ color: shade(look.body, 0.6) }).stroke(EDGE);
  for (const v of [-1, 1]) g.poly([...P(-2.2, v * 0.4), ...P(-2.8, v * 1.2), ...P(-3.0, v * 1.2), ...P(-2.5, v * 0.3)]).fill({ color: shade(look.body, 0.75) }).stroke(EDGE);
  // fuselage: an ogive nose, a parallel body and a boat-tail narrowing to the nozzle
  const body = [...P(3.4, 0), ...P(2.2, -0.55), ...P(-1.8, -0.55), ...P(-2.7, -0.4), ...P(-2.7, 0.4), ...P(-1.8, 0.55), ...P(2.2, 0.55)];
  g.poly(body).fill({ color: look.body }).stroke(EDGE);
  g.poly([...P(3.4, 0), ...P(2.2, 0.05), ...P(-1.8, 0.05), ...P(-2.7, 0.4), ...P(-1.8, 0.55), ...P(2.2, 0.55)]).fill({ color: shade(look.body, 0.6), alpha: 0.85 });
  g.poly([...P(2.2, -0.55), ...P(-1.8, -0.55), ...P(-1.8, -0.35), ...P(2.2, -0.35)]).fill({ color: shade(look.body, 1.18), alpha: 0.8 });
  // a warning band near the nose, the seeker window at its tip, and an intake on the flank
  const [bx, by] = P(1.5, -0.55);
  g.rect(bx, by, r * 0.25, r * 1.1).fill({ color: 0xd94a3a });
  const [tx, ty] = P(3.0, 0);
  g.circle(tx, ty, r * 0.12).fill({ color: 0x7fd6ff, alpha: 0.9 });
  const [ix, iy] = P(-0.6, 0.55);
  g.rect(ix, iy, r * 1.0, r * 0.22).fill({ color: 0x14171a }).stroke(EDGE);
  // the nozzle
  const [nx, ny] = P(-2.95, -0.3);
  g.rect(nx, ny, r * 0.35, r * 0.6).fill({ color: 0x2b2f33 }).stroke(EDGE);
}

/** A foot soldier, with a look per type: helmet or not, a pack, a scope, or civilian clothes. */
function drawFoot(g: Graphics, sx: number, sy: number, look: { body: number; r: number; gun: number; wide: number; helmet: number | null; scope?: boolean; pack?: boolean }, dir: number, u: { id: number; type: string }, lift: number): void {
  const r = look.r;
  // every point is given in body units from the feet: f forward (toward the way it faces), v up
  const at = (f: number, v: number): [number, number] => [sx + dir * f * r, sy - v * r];
  const poly = (pts: [number, number][], color: number, alpha = 1, edge = true) => {
    const flat = pts.flatMap(([x, y]) => [x, y]);
    const shape = g.poly(flat).fill({ color, alpha });
    if (edge) shape.stroke(EDGE);
    return shape;
  };
  // a shadow on the ground, away from a flier's body
  g.ellipse(sx, sy + lift, r * 1.1, r * 0.5).fill({ color: 0x000000, alpha: 0.4 });
  // legs: tapered trousers, the far leg a step back; boots with a lit toe
  const stride = u.id % 2 ? 0.25 : -0.25;
  const legNear = [at(-0.25, 1.3), at(0.25, 1.3), at(0.22 + stride, 0.12), at(-0.12 + stride, 0.12)];
  const legFar = [at(-0.3 - stride * 0.5, 1.3), at(0.05 - stride * 0.5, 1.3), at(0.02 - stride * 0.6, 0.1), at(-0.25 - stride * 0.6, 0.1)];
  poly(legFar, shade(TROUSERS, 0.7));
  poly(legNear, TROUSERS);
  // knee highlight on the lit side
  poly([at(-0.2, 0.95), at(-0.05, 0.95), at(-0.05, 0.65), at(-0.2, 0.65)], shade(TROUSERS, 1.5), 0.6, false);
  poly([at(-0.2 + stride, 0.12), at(0.18 + stride, 0.12), at(0.24 + stride, -0.05), at(-0.22 + stride, -0.05)], 0x17130f);
  poly([at(-0.25 - stride * 0.6, 0.1), at(0.0 - stride * 0.6, 0.1), at(0.05 - stride * 0.6, -0.04), at(-0.28 - stride * 0.6, -0.04)], 0x0f0c0a);
  // torso: a tapered tunic, wider at the shoulders, lit on the front and shaded behind; shoulders widen with `wide`
  const sh = 0.5 * look.wide;
  const torso = [at(-sh, 2.0), at(sh, 2.0), at(0.42, 1.25), at(0.36, 0.85), at(-0.36, 0.85), at(-0.42, 1.25)];
  poly(torso, shade(look.body, 0.95));
  poly([at(0.05, 2.0), at(sh, 2.0), at(0.42, 1.25), at(0.36, 0.85), at(0.05, 0.85)], shade(look.body, 0.62), 0.75, false);
  poly([at(-sh, 2.0), at(0.02, 2.0), at(0.02, 1.25), at(-0.42, 1.25)], shade(look.body, 1.18), 0.55, false);
  // a belt at the waist, with a buckle
  poly([at(-0.37, 0.95), at(0.37, 0.95), at(0.37, 0.84), at(-0.37, 0.84)], 0x2a2320, 1, false);
  poly([at(0.02, 0.95), at(0.1, 0.95), at(0.1, 0.84), at(0.02, 0.84)], 0x9a8a5a, 1, false);
  // a plate carrier over the chest with a row of pouches, and dirt on the uniform
  poly([at(-sh * 0.7, 1.9), at(sh * 0.7, 1.9), at(0.34, 1.3), at(-0.34, 1.3)], 0x23272a);
  for (let k = 0; k < 3; k++) poly([at(-0.28 + k * 0.22, 1.5), at(-0.1 + k * 0.22, 1.5), at(-0.1 + k * 0.22, 1.2), at(-0.28 + k * 0.22, 1.2)], 0x17191c, 1, false);
  for (let k = 0; k < 6; k++) g.circle(...at(-0.5 + rand(u.id, k + 20) * 1.0, 0.9 + rand(u.id, k + 30) * 1.1), r * 0.05).fill({ color: 0x1c1a16, alpha: 0.6 });
  // the arms: the near arm reaches to the grip, the far arm behind the body
  poly([at(0.4, 1.9), at(0.62, 1.4), at(0.5, 0.95), at(0.3, 1.2)], shade(look.body, 0.8));
  poly([at(-0.42, 1.9), at(-0.6, 1.4), at(-0.52, 1.0), at(-0.36, 1.25)], shade(look.body, 0.6));
  // the head: a face with its shadow side, and a helmet dome with a brim, or hair
  const [hx, hy] = at(0, 2.45);
  g.circle(hx, hy, r * 0.4).fill({ color: SKIN }).stroke(EDGE);
  g.ellipse(hx + dir * r * 0.14, hy + r * 0.04, r * 0.24, r * 0.34).fill({ color: shade(SKIN, 0.7), alpha: 0.5 });
  if (look.helmet !== null) {
    const dome: [number, number][] = [];
    for (let k = 0; k <= 10; k++) {
      const t = Math.PI * (k / 10);
      dome.push([hx - Math.cos(t) * r * 0.5 * dir, hy - r * 0.05 - Math.sin(t) * r * 0.42]);
    }
    poly([...dome, [hx + dir * r * 0.55, hy - r * 0.02], [hx - dir * r * 0.5, hy - r * 0.02]], look.helmet);
    poly([[hx - r * 0.56, hy - r * 0.02], [hx + r * 0.56, hy - r * 0.02], [hx + r * 0.5, hy + r * 0.07], [hx - r * 0.5, hy + r * 0.07]], shade(look.helmet, 0.6), 1, false);
    // a chin strap
    g.moveTo(hx - r * 0.2, hy + r * 0.12).lineTo(hx + dir * r * 0.05, hy + r * 0.36).stroke({ color: 0x14110e, width: 0.8 });
  } else {
    g.ellipse(hx, hy - r * 0.22, r * 0.44, r * 0.2).fill({ color: 0x3b2a1e });
  }
  // the rifle held across the body: a stock, a receiver, a barrel and a magazine, all tapered
  const grip = at(0.15, 1.2);
  const muzzleLen = look.gun * 0.32;
  poly([at(-0.55, 1.3), at(-0.3, 1.36), at(-0.3, 1.14), at(-0.55, 1.12)], 0x3b2f22);            // stock
  poly([at(-0.3, 1.38), at(0.3, 1.38), at(0.3, 1.14), at(-0.3, 1.14)], 0x262a2e);                // receiver
  poly([at(0.3, 1.3), at(0.3 + muzzleLen, 1.3), at(0.3 + muzzleLen, 1.22), at(0.3, 1.2)], 0x1d2024); // barrel
  poly([at(0.12, 1.14), at(0.24, 1.14), at(0.2, 0.8), at(0.08, 0.8)], 0x1d2024);                   // magazine
  poly([at(-0.14, 1.14), at(0.0, 1.14), at(-0.02, 0.9), at(-0.12, 0.9)], 0x3a3f45, 1, false);   // grip
  if (look.scope) {
    // a long scope on mounts with a lens that catches the light, and a bipod under the barrel
    poly([at(0.02, 1.5), at(0.85, 1.5), at(0.85, 1.42), at(0.02, 1.42)], 0x14171a);
    g.circle(...at(0.92, 1.46), r * 0.1).fill({ color: 0x7fd6ff, alpha: 0.9 });
    g.moveTo(...at(0.55, 1.22)).lineTo(...at(0.45, 0.8)).stroke({ color: 0x1d2024, width: 1 });
    g.moveTo(...at(0.55, 1.22)).lineTo(...at(0.65, 0.8)).stroke({ color: 0x1d2024, width: 1 });
  } else {
    poly([at(0.02, 1.42), at(0.2, 1.42), at(0.2, 1.36), at(0.02, 1.36)], 0x14171a, 1, false); // red-dot optic
  }
  if (u.type === 'heavy_gunner') {
    // a light machine gun: receiver, a vented shroud, a box magazine, and a belt running to the pack
    poly([at(-0.2, 1.42), at(0.8, 1.42), at(0.8, 1.26), at(-0.2, 1.26)], 0x2b2f33);
    poly([at(0.4, 1.36), at(1.2, 1.36), at(1.2, 1.2), at(0.4, 1.2)], 0x3a3f45);
    for (let k = 0; k < 4; k++) poly([at(0.5 + k * 0.16, 1.34), at(0.56 + k * 0.16, 1.34), at(0.56 + k * 0.16, 1.22), at(0.5 + k * 0.16, 1.22)], 0x14171a, 1, false);
    poly([at(-0.05, 1.14), at(0.22, 1.14), at(0.22, 0.72), at(-0.05, 0.72)], 0x1d2024);
    for (let k = 0; k < 7; k++) {
      const t = k / 6;
      g.circle(...at(-0.6 * (1 - t) + 0.1 * t, 1.2 - 0.5 * t - 0.2 * Math.sin(t * Math.PI)), r * 0.1).fill({ color: 0xc9a24a, alpha: 0.95 });
    }
  }
  if (u.type === 'conscript') {
    // a canvas satchel on the hip
    poly([at(-0.5, 1.05), at(-0.1, 1.05), at(-0.1, 0.6), at(-0.5, 0.6)], 0x6b4a2e);
    poly([at(-0.5, 1.05), at(-0.1, 1.05), at(-0.1, 0.92), at(-0.5, 0.92)], 0x4a3220, 1, false);
  }
  if (u.type === 'marksman') {
    // a ghillie speckle over the shoulders and chest
    for (let k = 0; k < 9; k++) g.circle(...at(-0.45 + rand(u.id, k) * 0.9, 1.2 + rand(u.id, k + 9) * 0.8), r * 0.07).fill({ color: 0x2a3a24, alpha: 0.85 });
  }
  void grip;
}

const NONE: ReadonlySet<number> = new Set();

/** Draws every living unit, interpolated between two snapshots. The selected one gets a ring and its bar shown even at full health. */
export function drawUnits(g: Graphics, prev: Snapshot, next: Snapshot, t: number, selection: ReadonlySet<number> = NONE, now = performance.now()): void {
  g.clear();
  // the defenders' roaming units are drawn with the attackers, in the defender's colour
  const prevById = new Map([...prev.units, ...prev.patrols].map(u => [u.id, u]));
  // back to front so overlapping soldiers read correctly
  const list = [...next.units, ...next.patrols].sort((a, b) => depth(a.x, a.y) - depth(b.x, b.y));
  for (const u of list) {
    const p = prevById.get(u.id) ?? u;
    const base = UNIT_LOOK[u.type] ?? UNIT_LOOK.rifleman!;
    const scaled = { ...base, r: base.r * UNIT_SCALE, gun: base.gun * UNIT_SCALE };
    const look = u.defender ? { ...scaled, body: DEFENDER_BODY } : scaled;
    // spread units that share a spot so a squad reads as a squad
    const jx = ((u.id * 37) % 7) - 3, jy = ((u.id * 53) % 5) - 2;
    const [sx0, sy0] = unitsToScreen(p.x + (u.x - p.x) * t, p.y + (u.y - p.y) * t);
    const lift = AIRBORNE.has(u.type) ? TILE_H * 2.2 : 0;
    const sx = sx0 + jx, sy = sy0 + jy * 0.6 - lift;
    // face the way the unit is moving across the screen
    const dir = unitsToScreen(u.x, u.y)[0] - unitsToScreen(p.x, p.y)[0] >= 0 ? 1 : -1;
    const selected = selection.has(u.id);
    if (u.disabled) g.circle(sx, sy - look.r * 3.8, 2.2).fill({ color: EMP_COLOR, alpha: 0.6 + 0.4 * Math.sin(now / 70) });
    if (selected) g.ellipse(sx, sy, look.r + 4, (look.r + 4) / 2).stroke({ color: 0xffffff, width: 1.5 });
    if (SHIPS.has(u.type)) {
      drawHull(g, sx, sy, look, dir, now, u.type === 'destroyer');
    } else if (VEHICLES.has(u.type)) {
      drawTank(g, sx, sy, look, dir);
    } else if (u.type === 'attack_heli' || JETS.has(u.type) || PROP_PLANES.has(u.type)) {
      drawHeli(g, sx, sy, look, dir, now);
    } else if (DRONES.has(u.type)) {
      drawDrone(g, sx, sy, look, dir, now, u.type === 'attack_drone');
    } else if (MISSILES.has(u.type)) {
      drawMissile(g, sx, sy, look, dir, now);
    } else {
      drawFoot(g, sx, sy, look, dir, u, lift);
    }
    if (u.hp < u.maxHp || selected) {
      g.rect(sx - 5, sy - look.r * 3.4, 10, 1.5).fill({ color: 0x000000, alpha: 0.5 });
      g.rect(sx - 5, sy - look.r * 3.4, 10 * (u.hp / u.maxHp), 1.5).fill({ color: 0xffffff, alpha: 0.85 });
    }
  }
}

/** How a unit looks in the 3D look: its colour, its shape family, and how high it flies (tiles). */
export function unit3DStyle(u: { type: string; defender?: boolean }): { color: number; kind: 'foot' | 'vehicle' | 'ship' | 'air' | 'drone' | 'missile'; lift: number } {
  const color = u.defender ? DEFENDER_BODY : (UNIT_LOOK[u.type] ?? UNIT_LOOK.rifleman!).body;
  const kind = SHIPS.has(u.type) ? 'ship' : VEHICLES.has(u.type) ? 'vehicle'
    : u.type === 'attack_heli' || JETS.has(u.type) || PROP_PLANES.has(u.type) ? 'air'
    : DRONES.has(u.type) ? 'drone'
    : MISSILES.has(u.type) ? 'missile' : 'foot';
  return { color, kind, lift: JETS.has(u.type) ? 2.2 : PROP_PLANES.has(u.type) ? 1.3 : AIRBORNE.has(u.type) ? 0.9 : 0 };
}

/** The unit layer, which the 3D look replaces with 3D units. */
export function paintUnits(g: Graphics, prev: Snapshot, next: Snapshot, t: number, selected: ReadonlySet<number> = NONE, now = performance.now()): void {
  if (is3D()) { setUnits3D({ prev, next, t, selected }); g.clear(); return; }
  drawUnits(g, prev, next, t, selected, now);
}

/** Tracers in the 3D look are lines in the scene; the 2D layer stays empty. */
export function paintShots(g: Graphics, shots: readonly LiveShot[], now: number): void {
  if (is3D()) { setShots3D(shots); g.clear(); return; }
  drawShots(g, shots, now);
}

/** Deaths in the 3D look are flat marks and blasts in the scene. */
export function paintDeaths(g: Graphics, deaths: readonly DeathFx[], now: number, clear = true): void {
  if (is3D()) { setDeaths3D(deaths); if (clear) g.clear(); return; }
  drawDeaths(g, deaths, now, clear);
}

/** Smoke in the 3D look is puffs in the scene. */
export function paintSmoke(g: Graphics, puffs: readonly SmokePuff[], now: number): void {
  if (is3D()) { setSmoke3D(puffs); g.clear(); return; }
  drawSmoke(g, puffs, now);
}

// ---- tracers -------------------------------------------------------------------------

export interface LiveShot extends Shot { at: number }
export const TRACER_MS = 140;
/** A missile in flight takes longer to cross the screen than a bullet. */
export const MISSILE_MS = 900;
/** How long a shot stays on screen: missiles and interceptions linger, bullets flash past. */
export function shotLife(type: string): number {
  return MISSILE_LAUNCHERS.has(type) || MISSILE_SHOTS.has(type) || type === 'ship_intercept' || type === 'howitzer' ? MISSILE_MS : TRACER_MS;
}

/** Electronics knocked out by an EMP, and the EMP blast itself. */
export const EMP_COLOR = 0x6fe3ff;

export function drawShots(g: Graphics, shots: readonly LiveShot[], now: number): void {
  g.clear();
  for (const s of shots) {
    const age = (now - s.at) / shotLife(s.type);
    if (age >= 1 || s.type === 'blast' || s.type === 'ac130_fire') continue;
    const [fx, fy] = unitsToScreen(s.fromX, s.fromY);
    const [tx, ty] = unitsToScreen(s.toX, s.toY);
    const lift = s.by === 'defence' ? TILE_H * 1.2 : TILE_H * 0.4;
    if (s.by === 'strike') {
      // a blast ring grows out from the landing point; a nuke also flashes
      const grow = Math.min(1, age * 2.5);
      const nuke = s.type === 'nuke';
      const r = (nuke ? 46 : 28) * grow;
      const color = nuke ? 0xffd27a : EMP_COLOR;
      if (nuke) g.circle(fx, fy, r).fill({ color: 0xfff1b8, alpha: (1 - age) * 0.35 });
      g.circle(fx, fy, r).stroke({ color, width: nuke ? 2.4 : 1.4, alpha: 1 - age });
      continue;
    }
    if (s.type === 'ship_intercept') {
      // a ship's point defence downs an incoming missile: a bright burst in the air over the ship
      g.circle(fx, fy - TILE_H * 2, 6 + age * 10).fill({ color: 0xfff1b8, alpha: 1 - age });
      g.circle(fx, fy - TILE_H * 2, 3 + age * 16).stroke({ color: 0xffb040, width: 1.5, alpha: 1 - age });
      continue;
    }
    if (MISSILE_LAUNCHERS.has(s.type) || MISSILE_SHOTS.has(s.type) || s.type === 'howitzer') {
      // a missile or shell on a high arc: a bright head with a hot tail, flying from launcher to target
      const k = Math.min(1, age * 1.2);
      const arc = s.type === 'howitzer' ? 56 : s.type === 'mortar' ? 46 : s.type === 'himars' || s.type === 'avenger' ? 22 : 30;
      const hx = fx + (tx - fx) * k, hy = fy - TILE_H * 1.5 + (ty - fy + TILE_H * 1.5) * k - Math.sin(k * Math.PI) * arc;
      g.circle(hx, hy, s.type === 'howitzer' || s.type === 'mortar' ? 3 : 2.4).fill({ color: 0xffffff, alpha: 1 - age * 0.5 });
      g.circle(hx, hy, s.type === 'howitzer' || s.type === 'mortar' ? 5.5 : 4.5).fill({ color: 0xffa040, alpha: 0.5 * (1 - age) });
      if ((s.type === 'howitzer' || s.type === 'mortar') && k >= 0.95) {
        const b = (age - 0.75) / 0.25;
        if (b > 0) g.circle(tx, ty, 24 * Math.min(1, b)).stroke({ color: 0xffb040, width: 2, alpha: Math.max(0, 1 - b) });
      }
      if (s.type === 'himars' || s.type === 'avenger') {
        // a smoking motor: a grey trail puffs out behind the head
        const bx0 = fx + (tx - fx) * Math.max(0, k - 0.12), by0 = fy - TILE_H * 1.5 + (ty - fy + TILE_H * 1.5) * Math.max(0, k - 0.12) - Math.sin(Math.max(0, k - 0.12) * Math.PI) * arc;
        g.circle(bx0, by0, 4 + age * 6).fill({ color: 0xcfcfc4, alpha: 0.35 * (1 - age) });
      }
      continue;
    }
    if (s.type === 'hpm') {
      // a microwave pulse: a pale ring racing out over its reach
      const r = ((s.blast ?? TILE * 9) / TILE) * TILE_W * 0.5 * Math.min(1, age * 3);
      g.ellipse(fx, fy, r, r / 2).stroke({ color: 0x9fe8ff, width: 2, alpha: (1 - age) * 0.7 });
      continue;
    }
    if (s.type === 'iron_beam') {
      // a laser: the beam burns between the mounts for an instant and dies at the target in a hot flash
      const k = Math.min(1, age * 4);
      const ax = fx, ay = fy - TILE_H * 1.4;
      const bx = fx + (tx - fx) * k, by = ay + (ty - TILE_H * 0.3 - ay) * k;
      g.moveTo(ax, ay).lineTo(bx, by).stroke({ color: 0xff4038, width: 2.6, alpha: (1 - age) * 0.9 });
      g.moveTo(ax, ay).lineTo(bx, by).stroke({ color: 0xffffff, width: 1, alpha: (1 - age) * 0.9 });
      if (age < 0.35) g.circle(tx, ty, 7 + age * 12).fill({ color: 0xffd27a, alpha: 1 - age / 0.35 });
      continue;
    }
    if (s.type === 'c_ram') {
      // a C-RAM streams small, fast rounds: a short bright dash racing from the barrels to the target
      const k0 = Math.min(1, age * 3), k1 = Math.max(0, k0 - 0.18);
      const ax = fx + (tx - fx) * k1, ay = fy - TILE_H * 1.6 + (ty - TILE_H * 0.3 - fy + TILE_H * 1.6) * k1;
      const bx = fx + (tx - fx) * k0, by = fy - TILE_H * 1.6 + (ty - TILE_H * 0.3 - fy + TILE_H * 1.6) * k0;
      g.moveTo(ax, ay).lineTo(bx, by).stroke({ color: 0xffe9a8, width: 1, alpha: 1 - age });
      continue;
    }
    const color = s.by === 'defence' ? 0xffd27a : 0xd9ffd0;
    // the tracer is a short dash that travels from shooter to target
    const k0 = Math.min(1, age * 1.4), k1 = Math.max(0, k0 - 0.35);
    const ax = fx + (tx - fx) * k1, ay = fy - lift + (ty - TILE_H * 0.3 - fy + lift) * k1;
    const bx = fx + (tx - fx) * k0, by = fy - lift + (ty - TILE_H * 0.3 - fy + lift) * k0;
    g.moveTo(ax, ay).lineTo(bx, by).stroke({ color, width: s.by === 'defence' ? 1.6 : 1.1, alpha: 1 - age });
    if (age < 0.3) g.circle(fx, fy - lift, s.by === 'defence' ? 3 : 2).fill({ color: 0xfff1b8, alpha: 1 - age / 0.3 });
  }
}

export function snapshotToDrawables(s: Snapshot): DrawableBuilding[] {
  // mines are hidden: the attacker never sees them, only the result, unless a recon drone found them
  return s.buildings.filter(b => !MINES.has(b.type) || b.revealed).map(b => ({
    type: b.type, x: b.x, y: b.y, size: b.size, hpFrac: b.hp / b.maxHp, seed: b.id, disabled: b.disabled,
    ...(b.level !== undefined ? { level: b.level } : {}),
    state: b.hp > 0 ? 'normal' : 'dead',
  }));
}


// ---- deaths: a splatter for foot soldiers, a fireball for machines ---------------------

/** Units that are people. Their deaths are gore; everything else blows up. */
export const FOOT = new Set(['rifleman', 'marksman', 'heavy_gunner', 'conscript', 'at_team']);

/** A unit that just died: where, and whether it was a person or a machine. */
export interface DeathFx { x: number; y: number; kind: 'flesh' | 'machine' | 'debris'; at: number; seed: number; /** What died, for the wreck's size and colour. */ type?: string; /** Killed by a nuke: its ground zero (sim units), which throws the body across the map. */ blast?: { x: number; y: number }; /** The way the killing round was going (unit vector), for the spray off the exit wound. */ dir?: { x: number; y: number } }

/** How long a death effect lasts, in milliseconds: long enough for a body to fly, land and settle. */
export const DEATH_MS = 2800;


/** A rectangle turned by `ang` radians about its centre: for limbs that tumble. */
function rotRect(g: Graphics, cx: number, cy: number, w: number, h: number, ang: number, color: number, alpha = 1): void {
  const c = Math.cos(ang), s = Math.sin(ang);
  const pts: number[] = [];
  for (const [u, v] of [[-w / 2, -h / 2], [w / 2, -h / 2], [w / 2, h / 2], [-w / 2, h / 2]] as const) {
    pts.push(cx + u * c - v * s, cy + u * s + v * c);
  }
  g.poly(pts).fill({ color, alpha });
}

/**
 * A soldier blown apart: the head, torso, legs and arms are thrown out from where he stood, fly
 * under gravity, tumble, bounce once and come to rest on the ground. Positions come from the
 * time since the death, so it is smooth and costs nothing to keep in step.
 */
function drawRagdoll(g: Graphics, d: DeathFx, age: number, life: number): void {
  const [sx, sy] = unitsToScreen(d.x, d.y);
  const G = 1500;              // screen pixels per second squared
  const parts: { w: number; h: number; color: number; kind: 'head' | 'rect' }[] = [
    { w: 0, h: 0, color: SKIN, kind: 'head' },            // head
    { w: 7, h: 6, color: 0x4f5a3c, kind: 'rect' },         // torso
    { w: 3, h: 8, color: 0x23272a, kind: 'rect' },         // leg
    { w: 3, h: 8, color: 0x23272a, kind: 'rect' },         // leg
    { w: 2.5, h: 6, color: 0x4f5a3c, kind: 'rect' },       // arm
    { w: 2.5, h: 6, color: 0x4f5a3c, kind: 'rect' },       // arm
  ];
  parts.forEach((p, k) => {
    // thrown outward and up, with a random spread and speed
    const a = -Math.PI / 2 + (rand(d.seed, k) - 0.5) * 2.6;
    const v = 110 + rand(d.seed, k + 10) * 150;
    let x = sx + Math.cos(a) * v * age;
    let y = sy + Math.sin(a) * v * age + 0.5 * G * age * age;
    // bounce once off the ground, losing most of the speed, then lie still
    const floor = sy + 2 + rand(d.seed, k + 20) * 2;
    if (y > floor) {
      const dt = Math.max(0, age - Math.sqrt((2 * (floor - sy)) / G));
      y = floor - Math.abs(Math.sin(dt * 9)) * 2 * Math.exp(-dt * 4);
      x += Math.cos(a) * v * 0.15 * Math.min(dt, 0.4);
    }
    const spin = rand(d.seed, k + 30) * 6 + age * (rand(d.seed, k + 40) - 0.5) * 14;
    if (p.kind === 'head') {
      g.circle(x, y, 2.6).fill({ color: p.color, alpha: life });
      g.ellipse(x, y - 1, 2.8, 1.6).fill({ color: 0x2a2f24, alpha: life });
    } else {
      rotRect(g, x, y, p.w, p.h, spin, p.color, life);
    }
  });
}

/**
 * Blood pools and droplets for foot soldiers; a fireball and smoke for machines. Blood goes out the
 * way the bullet was going (the exit wound throws the spray), dries darker over a second or two, and
 * a few drops outrun the rest like a cut artery, coming down a moment later where the heart last sent them.
 */
export function drawDeaths(g: Graphics, deaths: readonly DeathFx[], now: number, clear = true): void {
  if (clear) g.clear();
  for (const d of deaths) {
    const age = (now - d.at) / 1000;
    if (age * 1000 > DEATH_MS) continue;
    const life = 1 - (age * 1000) / DEATH_MS;
    const [sx, sy] = unitsToScreen(d.x, d.y);
    if (d.kind === 'flesh') {
      // the body comes apart and lands in pieces
      drawRagdoll(g, d, age, life);
      // the bullet's line, in screen space: the spray follows it, not the wind
      const [ex, ey] = unitsToScreen(d.x + (d.dir?.x ?? 0) * 0.05, d.y + (d.dir?.y ?? 0) * 0.05);
      const wx = ex - sx, wy = ey - sy;
      const base = Math.atan2(wy, wx);                     // exit wound is atan2 on screen: y grows downward
      const wd = Math.hypot(wx, wy) || 1;
      // the escaping spray: most of it out along the round's line, some in tight, a little backspatter
      for (let k = 0; k < 12; k++) {
        const spread = rand(d.seed, k) * 1.1 - 0.35;       // a fan about the exit line
        const along = base + spread * (k % 3 === 0 ? 1 : 0.55);
        const speed = 14 + rand(d.seed, k + 9) * 22;
        const reach = Math.min(age / 0.35, 1) * speed * (wd / 8 + 0.45);
        const px = sx + Math.cos(along) * reach, py = sy + Math.sin(along) * reach * 0.55 - (k % 4 === 0 ? age * 6 : 0);
        // arterial blood is bright for the first moments, then what lies dries to near-black
        const dry = Math.min(1, age / 1.6);
        const wet = mix(0x8f1419, 0x3c060b, dry);
        g.circle(px, py, 1.2 + rand(d.seed, k + 3) * 1.5).fill({ color: k % 3 === 0 ? mix(0xc2212a, 0x4a080d, dry) : wet, alpha: life });
      }
      // a fine mist carries through the air beyond the drops (half-heartbeats of a torn vessel)
      for (let k = 0; k < 5; k++) {
        const reach = Math.min(age / 0.25, 1) * (9 + rand(d.seed, k + 40) * 7);
        g.circle(sx + wx / (wd || 1) * reach + (rand(d.seed, k + 50) - 0.5) * 6, sy + wy / (wd || 1) * reach * 0.5 - 2, 2 + rand(d.seed, k + 60))
          .fill({ color: 0x7a0f14, alpha: 0.4 * (1 - age / 0.5) * life });
      }
      // the pool under him spreads, then darkens as it dries into the dust
      const dry = Math.min(1, age / 2.2);
      const pool = mix(0x8f1419, 0x33060a, dry);
      const r = 3 + Math.min(age, 0.6) * 12;
      g.ellipse(sx, sy + 1, r, r / 2).fill({ color: pool, alpha: 0.75 * life });
      g.ellipse(sx, sy + 1, r * 0.55, r * 0.28).fill({ color: mix(0x4a080d, 0x2a0508, dry), alpha: 0.9 * life });
    } else {
      // a machine tears apart: plates and sparks thrown out, falling and tumbling
      for (let k = 0; k < 6; k++) {
        const a = -Math.PI / 2 + (rand(d.seed, k + 50) - 0.5) * 3;
        const v = 80 + rand(d.seed, k + 60) * 140;
        const x = sx + Math.cos(a) * v * age;
        const y = Math.min(sy + 2, sy + Math.sin(a) * v * age + 0.5 * 1500 * age * age);
        rotRect(g, x, y, 4 + rand(d.seed, k + 70) * 3, 2 + rand(d.seed, k + 80) * 2, age * 8 + k, k % 2 ? 0x2b2f33 : 0x6d5b3a, life);
      }

      // the blast swells and fades, the core brightest, and smoke rises
      const r = 4 + Math.min(age, 0.35) * 70;
      g.circle(sx, sy - 6, r).fill({ color: 0xff8a2a, alpha: 0.9 * life });
      g.circle(sx, sy - 6, r * 0.5).fill({ color: 0xfff1b8, alpha: 0.9 * life });
      for (let k = 0; k < 4; k++) {
        const py = sy - 10 - age * (26 + k * 8);
        g.circle(sx + (rand(d.seed, k + 3) - 0.5) * 16, py, 4 + age * 7).fill({ color: 0x3a3a3a, alpha: 0.5 * life });
      }
    }
  }
}

// ---- smoke and incoming strikes ---------------------------------------------------------

/** A puff of smoke: a missile's trail, or the cloud a ship throws up when it launches. */
export interface SmokePuff { x: number; y: number; at: number; size: number; flash?: boolean; /** Part of the defender's smoke screen. */ cloud?: boolean }

/** How long a puff of smoke hangs in the air, in milliseconds. */
export const SMOKE_MS = 1400;

/** Smoke trails behind missiles and launch clouds at ships: puffs that swell, drift up and fade. A launch also flashes. */
export function drawSmoke(g: Graphics, puffs: readonly SmokePuff[], now: number): void {
  g.clear();
  for (const p of puffs) {
    const age = (now - p.at) / SMOKE_MS;
    if (age >= 1) continue;
    const [sx, sy] = unitsToScreen(p.x, p.y);
    const lift = TILE_H * 2.2 + age * 10;
    if (p.flash && age < 0.15) g.circle(sx, sy - TILE_H * 0.6, 10 * (1 - age / 0.15) + 4).fill({ color: 0xfff1b8, alpha: 0.9 * (1 - age / 0.15) });
    g.circle(sx, sy - lift, p.size * (0.6 + age * 1.6)).fill({ color: 0xc9c9c4, alpha: 0.45 * (1 - age) });
  }
}

/** A nuke or EMP counting down: a pulsing ring on its landing tile, quicker as it gets closer. */
export function drawIncoming(g: Graphics, incoming: readonly IncomingStrike[], now: number): void {
  // the 3D look marks them on the ground itself
  if (is3D()) { setIncoming3D(incoming); return; }
  for (const s of incoming) {
    const [sx, sy] = toScreen(s.x + 0.5, s.y + 0.5);
    const urgency = 1 + Math.max(0, 5 - s.left / 20);
    const pulse = 0.5 + 0.5 * Math.sin((now / 1000) * Math.PI * 2 * urgency);
    // the defender's own fire mission is amber; the enemy's nuke red and EMP blue
    const color = s.kind === 'nuke' ? 0xff3b2e : s.kind === 'artillery' ? 0xf0a830 : EMP_COLOR;
    const r = s.kind === 'nuke' ? 46 : s.kind === 'artillery' ? 24 : 28;
    g.ellipse(sx, sy, r, r / 2).stroke({ color, width: 2, alpha: 0.5 + 0.5 * pulse });
    g.ellipse(sx, sy, r * 0.4, r * 0.2).fill({ color, alpha: 0.25 + 0.3 * pulse });
  }
}
